import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import {
  MapPin, Search, X, Route, Plus, Trash2, Navigation, Loader2, Store, Upload, Check,
  List, MapPinned, Crosshair, FileDown, Flag, LayoutDashboard, Navigation2, Copy, UserPlus, StickyNote, Users,
  Phone, Mail, Globe, TrendingUp, TrendingDown, Palette, ShieldCheck, History, Sparkles, CalendarDays,
  CalendarClock, Send, BellRing, Home, ClipboardList, Menu, Star, Filter, ChevronUp, ChevronDown, EyeOff,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GKEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const FRANCE = { lat: 46.6, lng: 2.4 };

const SHAPE_PATH = {
  circle: (c) => `<circle cx="14" cy="14" r="9" fill="${c}" stroke="#fff" stroke-width="2.5"/>`,
  triangle: (c) => `<path d="M14 4 L25 24 L3 24 Z" fill="${c}" stroke="#fff" stroke-width="2"/>`,
  diamond: (c) => `<path d="M14 3 L25 14 L14 25 L3 14 Z" fill="${c}" stroke="#fff" stroke-width="2"/>`,
  star: (c) => `<path d="M14 3 l3.1 6.9 7.6.7-5.7 5 1.7 7.4L14 26l-6.4 3.9 1.7-7.4-5.7-5 7.6-.7Z" fill="${c}" stroke="#fff" stroke-width="1.5"/>`,
};
const iconUrl = (color, shape) => {
  const inner = (SHAPE_PATH[shape] || SHAPE_PATH.circle)(color);
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">${inner}</svg>`)}`;
};

// Logos des marques du réseau WASABI (WASABI + SORAE) — affichés à la place des points sur la carte.
const BRAND_LOGOS = { WASABI: '/logos/wasabi.png', SORAE: '/logos/sorae.png' };
const pointImg = (p) => (p && p.category === 'wasabi' && BRAND_LOGOS[p.brand]) ? BRAND_LOGOS[p.brand] : iconUrl(p.color, p.icon);

function haversine(a, b) {
  const R = 6371, toR = (d) => d * Math.PI / 180;
  const dLat = toR(b.lat - a.lat), dLng = toR(b.lng - a.lng);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

const FREQ_COLORS = ['#D9D6CE', '#A6A49E', '#6B6964', '#F26A1B']; // §6.4 rampe neutre chaude · 3×+ = orange (urgence)
const freqColor = (c) => FREQ_COLORS[Math.min(c || 0, 3)];

function MarkersLayer({ points, onSelect, freqView, countKey }) {
  const map = useMap();
  const clustererRef = useRef(null);
  useEffect(() => {
    if (!map || !window.google) return;
    const markers = points.map((p) => {
      const isBrandLogo = !freqView && p.category === 'wasabi' && BRAND_LOGOS[p.brand];
      const url = freqView ? iconUrl(freqColor(p[countKey] || 0), p.icon) : pointImg(p);
      const sz = isBrandLogo ? 32 : 26;
      const m = new window.google.maps.Marker({
        position: { lat: p.lat, lng: p.lng },
        icon: { url, scaledSize: new window.google.maps.Size(sz, sz) },
        title: `${p.brand} — ${p.name || p.city || ''}`,
      });
      m.addListener('click', () => onSelect(p));
      return m;
    });
    clustererRef.current = new MarkerClusterer({ map, markers });
    return () => { clustererRef.current && clustererRef.current.clearMarkers(); };
  }, [map, points, onSelect, freqView, countKey]);
  return null;
}

// Dessine un cercle de couverture correspondant au rayon choisi autour d'une adresse.
function RadiusCircle({ center, radiusKm }) {
  const map = useMap();
  const circleRef = useRef(null);
  useEffect(() => {
    if (!map || !window.google) return;
    if (circleRef.current) { circleRef.current.setMap(null); circleRef.current = null; }
    if (center && radiusKm > 0) {
      circleRef.current = new window.google.maps.Circle({
        map,
        center: { lat: center.lat, lng: center.lng },
        radius: radiusKm * 1000,
        strokeColor: '#25272B', strokeOpacity: 0.9, strokeWeight: 2,
        fillColor: '#25272B', fillOpacity: 0.10,
        clickable: false, zIndex: 1,
      });
      map.fitBounds(circleRef.current.getBounds());
    }
    return () => { if (circleRef.current) { circleRef.current.setMap(null); circleRef.current = null; } };
  }, [map, center, radiusKm]);
  return null;
}


// Gère le recentrage (flyTo), le marqueur de départ et le marqueur de recherche de zone.
function MapController({ flyTo, departure, searchMarker }) {
  const map = useMap();
  const depMarker = useRef(null);
  const srcMarker = useRef(null);
  useEffect(() => {
    if (map && flyTo) { map.panTo({ lat: flyTo.lat, lng: flyTo.lng }); map.setZoom(flyTo.zoom || 13); }
  }, [map, flyTo]);
  useEffect(() => {
    if (!map || !window.google) return;
    if (depMarker.current) { depMarker.current.setMap(null); depMarker.current = null; }
    if (departure) {
      depMarker.current = new window.google.maps.Marker({
        position: { lat: departure.lat, lng: departure.lng }, map,
        icon: { url: iconUrl('#F26A1B', 'star'), scaledSize: new window.google.maps.Size(32, 32) },
        title: `Départ : ${departure.label || ''}`, zIndex: 9999,
      });
    }
    return () => { if (depMarker.current) depMarker.current.setMap(null); };
  }, [map, departure]);
  useEffect(() => {
    if (!map || !window.google) return;
    if (srcMarker.current) { srcMarker.current.setMap(null); srcMarker.current = null; }
    if (searchMarker) {
      srcMarker.current = new window.google.maps.Marker({
        position: { lat: searchMarker.lat, lng: searchMarker.lng }, map,
        icon: { url: iconUrl('#25272B', 'star'), scaledSize: new window.google.maps.Size(34, 34) },
        title: `Recherche : ${searchMarker.label || ''}`, zIndex: 9998,
      });
    }
    return () => { if (srcMarker.current) srcMarker.current.setMap(null); };
  }, [map, searchMarker]);
  return null;
}

export default function CarteCornersPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  // Créneaux fixes du mode "Parc client WASABI" : 9h30 (matin) · 14h (après-midi)
  const FIXED_SLOTS = [9 * 60 + 30, 14 * 60];
  const ROLE_LABELS = {
    commercial: 'Commercial', directeur_commercial: 'Directeur Commercial',
    business_manager: 'Business Manager', directeur_regional: 'Directeur Régional (DR)',
    dop: 'Directeur des Opérations (DOP)', dg: 'Directeur Général',
    directrice_regionale: 'Directrice Régionale (DR)',
    admin: 'Administrateur', super_admin: 'Super Admin', readonly: 'Lecture seule', prestataire: 'Prestataire',
  };
  const roleLabel = ROLE_LABELS[user?.role] || (user?.role || '—');
  // BM / DR / DOP : organisation imposée sur le parc client WASABI (1 client matin / 1 aprem, 9h30 & 14h)
  const wasabiParcMode = ['business_manager', 'directeur_regional', 'directrice_regionale', 'dop'].includes(user?.role);
  const [points, setPoints] = useState([]);
  const [brands, setBrands] = useState({});
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showAccess, setShowAccess] = useState(false);
  const [accessCfg, setAccessCfg] = useState(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditType, setAuditType] = useState('');
  const [auditQ, setAuditQ] = useState('');
  const [showTournees, setShowTournees] = useState(false);
  const [tournees, setTournees] = useState(null);
  const [tourneesLoading, setTourneesLoading] = useState(false);
  const [tourneeBusy, setTourneeBusy] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [histCommercial, setHistCommercial] = useState('');
  const [histFrom, setHistFrom] = useState('');
  const [histTo, setHistTo] = useState('');
  const [mobileMenu, setMobileMenu] = useState(false);
  // Assistant de prospection intelligent
  const [showAssistant, setShowAssistant] = useState(false);
  const [asstBrands, setAsstBrands] = useState([]);
  const [asstDays, setAsstDays] = useState(['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi']);
  const [asstPerDay, setAsstPerDay] = useState(4);
  const [asstWeekStart, setAsstWeekStart] = useState('');
  const [asstCampaign, setAsstCampaign] = useState(false);
  const [asstWeeks, setAsstWeeks] = useState(3);
  const [asstInterval, setAsstInterval] = useState(2);
  const [asstUnvisited, setAsstUnvisited] = useState(false);
  const [asstMaxProposed, setAsstMaxProposed] = useState(''); // '' = tous ; '0' jamais ; '1' ≤1 ; '2' ≤2
  const [asstRadius, setAsstRadius] = useState(0);
  const [asstHours, setAsstHours] = useState({});
  const [asstVisitMin, setAsstVisitMin] = useState(30);
  const [asstLunch, setAsstLunch] = useState(true);
  const [asstLunchStart, setAsstLunchStart] = useState(12 * 60 + 30);
  const [asstLunchMin, setAsstLunchMin] = useState(60);
  const [asstRunning, setAsstRunning] = useState(false);
  const [gmsList, setGmsList] = useState([]);
  const [excludeGms, setExcludeGms] = useState(new Set());
  const toggleExcludeGms = (g) => setExcludeGms(prev => {
    const n = new Set(prev); n.has(g) ? n.delete(g) : n.add(g); return n;
  });
  // Envoi par email
  const [emailUserId, setEmailUserId] = useState('');
  const [emailTo, setEmailTo] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [activeBrands, setActiveBrands] = useState(null);
  const [search, setSearch] = useState('');
  const [center, setCenter] = useState(null);
  const [radius, setRadius] = useState(0);
  const [radiusAddr, setRadiusAddr] = useState('');
  const [radiusLoading, setRadiusLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [visits, setVisits] = useState([]);
  const [notes, setNotes] = useState([]);
  const [visitNote, setVisitNote] = useState('');
  const [noteText, setNoteText] = useState('');
  const [users, setUsers] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [route, setRoute] = useState([]);
  const [routeResult, setRouteResult] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [reimporting, setReimporting] = useState(false);
  const [showBrandCfg, setShowBrandCfg] = useState(false);
  const [savingStyle, setSavingStyle] = useState(false);
  const fileRef = useRef(null);
  const [importBrand, setImportBrand] = useState('KellyDeli');
  // Améliorations
  const [showList, setShowList] = useState(false);
  const [flyTo, setFlyTo] = useState(null);
  const [addMode, setAddMode] = useState(false);
  const [newPoint, setNewPoint] = useState(null);
  const [savingPoint, setSavingPoint] = useState(false);
  const [departure, setDeparture] = useState(null);
  const [depInput, setDepInput] = useState('');
  const [locating, setLocating] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Filtres avancés + tableau de bord
  const [fRegion, setFRegion] = useState('');
  const [fSecteur, setFSecteur] = useState('');
  const [fDept, setFDept] = useState('');
  const [fActivity, setFActivity] = useState('');
  const [fProposed, setFProposed] = useState(() => new Set()); // tranches "proposé par l'outil": '0','1','2','3+' (vide = toutes)
  const [freqView, setFreqView] = useState(false); // colorer les marqueurs par fréquence de proposition
  const [freqTeam, setFreqTeam] = useState(false); // (admin) baser la couleur sur le total équipe
  const [showTeam, setShowTeam] = useState(false);
  const [teamData, setTeamData] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamQ, setTeamQ] = useState('');
  const [showDash, setShowDash] = useState(false);
  const [dash, setDash] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false); // panneau filtres masqué par défaut sur mobile
  // Recherche géographique de zone (§10)
  const [geoSearch, setGeoSearch] = useState(null);
  const [geoRadius, setGeoRadius] = useState(20);
  const [geoLoading, setGeoLoading] = useState(false);
  // Plan multi-jours (§25.3)
  const [showPlan, setShowPlan] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planDays, setPlanDays] = useState(4);
  const [planPerDay, setPlanPerDay] = useState(4);

  const loadPoints = useCallback(() => {
    return fetch(`${BACKEND_URL}/api/carte-corners/points`, { headers: authHeaders })
      .then(r => r.json())
      .then(pts => setPoints((pts.points || []).filter(p => p.lat != null && p.lng != null)));
  }, [authHeaders]);

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/carte-corners/access`, { headers: authHeaders }).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/carte-corners/brands`, { headers: authHeaders }).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/carte-corners/points`, { headers: authHeaders }).then(r => r.json()),
    ]).then(([acc, br, pts]) => {
      setIsAdmin(!!acc.is_admin);
      setIsSuperAdmin(!!acc.is_super_admin);
      setBrands(br.brands || {});
      setActiveBrands(new Set(Object.keys(br.brands || {})));
      setPoints((pts.points || []).filter(p => p.lat != null && p.lng != null));
    }).catch(() => toast.error('Erreur de chargement')).finally(() => setLoading(false));
    fetch(`${BACKEND_URL}/api/carte-corners/users`, { headers: authHeaders })
      .then(r => r.json()).then(d => setUsers(d.users || [])).catch(() => {});
    fetch(`${BACKEND_URL}/api/carte-corners/log-view`, { method: 'POST', headers: authHeaders }).catch(() => {});
  }, [authHeaders]);

  const counts = useMemo(() => {
    const c = {};
    points.forEach(p => { c[p.brand] = (c[p.brand] || 0) + 1; });
    return c;
  }, [points]);

  const filterOpts = useMemo(() => {
    const regions = new Set(), depts = new Set();
    points.forEach(p => { if (p.region) regions.add(p.region); if (p.dept) depts.add(p.dept); });
    return {
      regions: [...regions].sort(),
      depts: [...depts].sort(),
    };
  }, [points]);

  const filtered = useMemo(() => {
    if (!activeBrands) return [];
    const q = search.trim().toLowerCase();
    const now = Date.now();
    const days = (n) => now - n * 86400000;
    return points.filter(p => {
      if (!activeBrands.has(p.brand)) return false;
      if (q && !`${p.name || ''} ${p.enseigne || ''} ${p.city || ''} ${p.postcode || ''}`.toLowerCase().includes(q)) return false;
      if (center && radius > 0 && haversine(center, p) > radius) return false;
      if (fRegion && p.region !== fRegion) return false;
      if (fSecteur && p.secteur !== fSecteur) return false;
      if (fDept && p.dept !== fDept) return false;
      if (fActivity) {
        const lv = p.last_visit ? new Date(p.last_visit).getTime() : null;
        if (fActivity === 'never' && lv) return false;
        if (fActivity === 'visited' && !lv) return false;
        if (fActivity === 'lt30' && (!lv || lv < days(30))) return false;
        if (fActivity === 'lt90' && (!lv || lv < days(90))) return false;
        if (fActivity === 'gt6m' && (!lv || lv >= days(182))) return false;
      }
      if (fProposed.size > 0) {
        const c = p.my_proposed_count || 0;
        const bucket = c === 0 ? '0' : c === 1 ? '1' : c === 2 ? '2' : '3+';
        if (!fProposed.has(bucket)) return false;
      }
      return true;
    });
  }, [points, activeBrands, search, center, radius, fRegion, fSecteur, fDept, fActivity, fProposed]);

  const toggleBrand = (b) => setActiveBrands(prev => {
    const n = new Set(prev); n.has(b) ? n.delete(b) : n.add(b); return n;
  });
  const allBrandsSelected = activeBrands && Object.keys(brands).length > 0 && Object.keys(brands).every(b => activeBrands.has(b));
  const toggleAllBrands = () => {
    const all = Object.keys(brands);
    setActiveBrands(prev => (prev && all.length && all.every(b => prev.has(b))) ? new Set() : new Set(all));
  };
  const toggleProposed = (k) => setFProposed(prev => {
    const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n;
  });

  const selectPoint = useCallback((p) => {
    setSelected(p); setVisits([]); setNotes([]); setVisitNote(''); setNoteText('');
    fetch(`${BACKEND_URL}/api/carte-corners/points/${p.id}/visits`, { headers: authHeaders })
      .then(r => r.json()).then(d => { setVisits(d.visits || []); setNotes(d.notes || []); }).catch(() => {});
  }, [authHeaders]);

  const focusPoint = useCallback((p) => {
    selectPoint(p);
    setFlyTo({ lat: p.lat, lng: p.lng, _t: Date.now() });
  }, [selectPoint]);

  const saveVisit = async () => {
    if (!selected || !visitNote.trim()) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/points/${selected.id}/visit`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: visitNote }),
      });
      const d = await r.json();
      setVisits(v => [d.visit, ...v]); setVisitNote('');
      toast.success('Visite enregistrée');
    } catch { toast.error("Échec de l'enregistrement"); }
  };

  const saveNote = async () => {
    if (!selected || !noteText.trim()) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/points/${selected.id}/note`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: noteText }),
      });
      const d = await r.json();
      setNotes(n => [d.note, ...n]); setNoteText('');
      toast.success('Note ajoutée');
    } catch { toast.error("Échec de l'ajout de la note"); }
  };

  const assignCommercial = async (commercialId) => {
    if (!selected) return;
    setAssigning(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/points/${selected.id}/assign`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ commercial_id: commercialId || null }),
      });
      const d = await r.json();
      setSelected(s => ({ ...s, commercial: d.commercial, commercial_id: d.commercial_id }));
      setPoints(ps => ps.map(p => p.id === selected.id ? { ...p, commercial: d.commercial, commercial_id: d.commercial_id } : p));
      toast.success(commercialId ? `Affecté à ${d.commercial}` : 'Affectation retirée');
    } catch { toast.error("Échec de l'affectation"); }
    finally { setAssigning(false); }
  };

  const openItinerary = (p) => {
    const from = departure ? `${departure.lat},${departure.lng}/` : '';
    window.open(`https://www.google.com/maps/dir/${from}${p.lat},${p.lng}`, '_blank');
  };
  const copyAddress = (p) => {
    const addr = [p.address, p.postcode, p.city].filter(Boolean).join(', ');
    const ok = () => toast.success('Adresse copiée');
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(addr).then(ok).catch(() => fallbackCopy(addr, ok));
    } else {
      fallbackCopy(addr, ok);
    }
  };
  const fallbackCopy = (text, ok) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta); ok();
    } catch { toast.error('Copie impossible'); }
  };

  const openDashboard = () => {
    setShowDash(true); setDashLoading(true);
    fetch(`${BACKEND_URL}/api/carte-corners/dashboard`, { headers: authHeaders })
      .then(r => r.json()).then(setDash).catch(() => toast.error('Erreur tableau de bord'))
      .finally(() => setDashLoading(false));
  };

  // Recherche géographique : géocode le texte de recherche et centre la carte.
  const runGeoSearch = async () => {
    const q = search.trim();
    if (!q) { toast.info('Saisissez une ville, un code postal ou une adresse'); return; }
    setGeoLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/geocode`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: q }),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setGeoSearch({ lat: d.lat, lng: d.lng, label: q });
      setFlyTo({ lat: d.lat, lng: d.lng, zoom: 10, _t: Date.now() });
      toast.success(`Zone centrée sur « ${q} »`);
    } catch { toast.error('Zone introuvable'); }
    finally { setGeoLoading(false); }
  };

  const geoResults = useMemo(() => {
    if (!geoSearch) return null;
    const near = points
      .filter(p => activeBrands?.has(p.brand))
      .map(p => ({ ...p, _dist: haversine(geoSearch, p) }))
      .filter(p => p._dist <= geoRadius)
      .sort((a, b) => a._dist - b._dist);
    return {
      list: near,
      wasabi: near.filter(p => p.category === 'wasabi').length,
      concurrents: near.filter(p => p.category === 'concurrent').length,
      non_visites: near.filter(p => p.category === 'concurrent' && !p.last_visit).length,
    };
  }, [geoSearch, geoRadius, points, activeBrands]);

  const addProjectAtSearch = () => {
    if (!geoSearch) return;
    setNewPoint({ lat: geoSearch.lat, lng: geoSearch.lng, brand: 'Projet signé', name: geoSearch.label || '', enseigne: '', address: '', postcode: '', city: geoSearch.label || '', comment: '' });
  };

  // Plan de tournée multi-jours.
  const buildPlan = async () => {
    if (route.length < 2) { toast.info('Ajoutez au moins 2 étapes'); return; }
    setPlanLoading(true); setShowPlan(true); setPlan(null);
    try {
      const body = {
        days: planDays, per_day: planPerDay,
        stops: route.map(p => ({ lat: p.lat, lng: p.lng, name: p.name || p.city, address: p.address, postcode: p.postcode, city: p.city })),
      };
      if (departure) body.origin = { lat: departure.lat, lng: departure.lng, name: 'Base' };
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/tournee/plan-multi-jours`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error();
      setPlan(await r.json());
    } catch { toast.error("Échec de la planification"); setShowPlan(false); }
    finally { setPlanLoading(false); }
  };
  const openDayInMaps = (day) => {
    const pts = (departure ? [departure] : []).concat(day.stops);
    const wp = pts.map(p => `${p.lat},${p.lng}`).join('/');
    window.open(`https://www.google.com/maps/dir/${wp}`, '_blank');
  };
  const exportPlanPdf = async () => {
    if (!plan) return;
    const stops = [];
    const legs = [];
    plan.days.forEach(day => {
      day.stops.forEach((s, i) => {
        stops.push({ name: `J${day.jour} · ${s.name || s.city || ''}`, address: s.address, postcode: s.postcode, city: s.city });
        legs.push(day.legs?.[i] || {});
      });
    });
    try {
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/tournee/export-pdf`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ stops, legs, distance_km: plan.total_distance_km, duration_min: plan.total_duration_min }),
      });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'plan-tournee.pdf'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Plan PDF téléchargé');
    } catch { toast.error("Échec de l'export"); }
  };
  const downloadBlob = async (endpoint, body, filename, okMsg) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/${endpoint}`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(okMsg);
    } catch { toast.error("Échec de l'export"); }
  };
  const exportPlanExcel = () => downloadBlob('tournee/export-excel', { plan }, 'tournee.xlsx', 'Excel téléchargé');
  const lunchArgs = () => (asstLunch ? { lunch_start: asstLunchStart, lunch_minutes: asstLunchMin } : {});
  // En mode Parc WASABI, les horaires sont fixes (9h30/14h) → on passe fixed_slots au backend (ICS/email).
  const icsScheduleArgs = () => wasabiParcMode
    ? { visit_minutes: 90, fixed_slots: FIXED_SLOTS }
    : { visit_minutes: asstVisitMin, ...lunchArgs() };
  const exportPlanIcs = () => downloadBlob('tournee/export-ics', { plan, ...icsScheduleArgs() }, 'tournee.ics', 'Fichier Outlook (.ics) téléchargé');
  const getDayHours = (d) => asstHours[d] || { start: 9, end: 18 };
  // Mode Parc WASABI : verrouille les enseignes sur WASABI, 2 visites/jour, sans pause déjeuner.
  useEffect(() => {
    if (showAssistant && wasabiParcMode) {
      setAsstBrands(['WASABI']);
      setAsstPerDay(2);
      setAsstLunch(false);
    }
  }, [showAssistant, wasabiParcMode]);

  // Options de semaine cible : lundi de la semaine courante + 11 semaines suivantes.
  const weekOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    const monday = new Date(now);
    const dow = (now.getDay() + 6) % 7; // 0 = lundi
    monday.setDate(now.getDate() - dow);
    monday.setHours(0, 0, 0, 0);
    for (let i = 0; i < 12; i++) {
      const m = new Date(monday);
      m.setDate(monday.getDate() + i * 7);
      const iso = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}-${String(m.getDate()).padStart(2, '0')}`;
      const fri = new Date(m); fri.setDate(m.getDate() + 4);
      const fmt = (dt) => dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
      opts.push({ iso, label: `Semaine du ${fmt(m)} au ${fmt(fri)}${i === 0 ? ' (cette semaine)' : ''}` });
    }
    return opts;
  }, [showAssistant]);

  useEffect(() => {
    if (!showAssistant || gmsList.length) return;
    (async () => {
      try {
        const [rg, rp] = await Promise.all([
          fetch(`${BACKEND_URL}/api/carte-corners/gms-enseignes`, { headers: authHeaders }),
          fetch(`${BACKEND_URL}/api/carte-corners/tour-prefs`, { headers: authHeaders }),
        ]);
        const dg = await rg.json();
        if (rg.ok) setGmsList(dg.gms || []);
        const dp = await rp.json();
        if (rp.ok && Array.isArray(dp.exclude_gms)) setExcludeGms(new Set(dp.exclude_gms));
      } catch (e) { /* silencieux */ }
    })();
  }, [showAssistant]); // eslint-disable-line react-hooks/exhaustive-deps

  const setDayHour = (d, field, val) => setAsstHours(prev => {
    const cur = prev[d] || { start: 9, end: 18 };
    const next = { ...cur, [field]: val };
    if (field === 'start' && next.end <= val) next.end = Math.min(val + 1, 23);
    return { ...prev, [d]: next };
  });
  const sendPlanEmail = async () => {
    if (!emailUserId && !emailTo.trim()) { toast.info('Choisissez un utilisateur ou saisissez un email'); return; }
    setSendingEmail(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/tournee/send-email`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan, commercial_id: emailUserId || undefined, recipient_email: emailTo.trim() || undefined, ...icsScheduleArgs() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      toast.success(`Tournée envoyée à ${d.recipient}`);
      setEmailTo(''); setEmailUserId('');
      setShowPlan(false);
    } catch (err) { toast.error(`Échec de l'envoi : ${err.message || ''}`); }
    finally { setSendingEmail(false); }
  };

  const dayScheduleInfo = (day) => {
    const stops = day.stops || [];
    const legs = day.legs || [];
    if (wasabiParcMode && stops.length) {
      const last = FIXED_SLOTS[Math.min(stops.length, FIXED_SLOTS.length) - 1];
      return { label: fmtHm(last + 90), over: false };
    }
    if (day.start_hour == null || stops.length === 0) return null;
    let cursor = day.start_hour * 60;
    let lunchTaken = !asstLunch;
    const lEnd = asstLunchStart + asstLunchMin;
    for (let i = 0; i < stops.length; i++) {
      cursor += (legs[i]?.duration_min) || (i > 0 ? 15 : 0);
      if (asstLunch && !lunchTaken && cursor < lEnd && (cursor + asstVisitMin) > asstLunchStart) { cursor = lEnd; lunchTaken = true; }
      else if (asstLunch && !lunchTaken && cursor >= lEnd) lunchTaken = true;
      cursor += asstVisitMin;
    }
    const endH = Math.floor(cursor / 60), endM = cursor % 60;
    const over = day.end_hour != null && cursor > day.end_hour * 60;
    return { label: `${endH}h${String(endM).padStart(2, '0')}`, over };
  };

  const fmtHm = (m) => `${Math.floor(m / 60)}h${String(Math.round(m % 60)).padStart(2, '0')}`;
  const dayAgenda = (day) => {
    const stops = day.stops || [];
    const legs = day.legs || [];
    if (wasabiParcMode) {
      if (stops.length === 0) return null;
      const rows = stops.map((_, i) => {
        const arrive = FIXED_SLOTS[i] != null ? FIXED_SLOTS[i] : (FIXED_SLOTS[FIXED_SLOTS.length - 1] + (i - FIXED_SLOTS.length + 1) * 90);
        return { legMin: legs[i]?.duration_min ?? null, legKm: legs[i]?.distance_km, arrive, depart: arrive + 90, lunchBefore: false };
      });
      return { rows, end: rows[rows.length - 1].depart, over: false };
    }
    if (day.start_hour == null || stops.length === 0) return null;
    let cursor = day.start_hour * 60;
    let lunchTaken = !asstLunch;
    const lEnd = asstLunchStart + asstLunchMin;
    const rows = [];
    for (let i = 0; i < stops.length; i++) {
      const legMin = (legs[i]?.duration_min) ?? (i > 0 ? 15 : 0);
      const legKm = legs[i]?.distance_km;
      cursor += legMin;
      let lunchBefore = false;
      if (asstLunch && !lunchTaken && cursor < lEnd && (cursor + asstVisitMin) > asstLunchStart) { cursor = lEnd; lunchTaken = true; lunchBefore = true; }
      else if (asstLunch && !lunchTaken && cursor >= lEnd) lunchTaken = true;
      const arrive = cursor;
      cursor += asstVisitMin;
      rows.push({ legMin, legKm, arrive, depart: cursor, lunchBefore });
    }
    const over = day.end_hour != null && cursor > day.end_hour * 60;
    return { rows, end: cursor, over };
  };

  const toggleAsstBrand = (b) => setAsstBrands(prev => prev.includes(b) ? prev.filter(x => x !== b) : [...prev, b]);
  const toggleAsstDay = (d) => setAsstDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  const runAssistant = async () => {
    if (!departure) { toast.info("Définissez d'abord un point de départ ci-dessous (adresse ou « Ma position »)"); return; }
    if (asstDays.length === 0) { toast.info('Sélectionnez au moins un jour'); return; }
    setAsstRunning(true);
    try {
      const campaign = asstCampaign;
      const endpoint = campaign ? 'tournee/prospection-campagne' : 'tournee/prospection';
      const weekStart = campaign ? (asstWeekStart || weekOptions[0]?.iso) : (asstWeekStart || undefined);
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/${endpoint}`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origin: { lat: departure.lat, lng: departure.lng },
          brands: wasabiParcMode ? ['WASABI'] : asstBrands,
          exclude_gms: wasabiParcMode ? [] : Array.from(excludeGms),
          days: asstDays,
          per_day: wasabiParcMode ? 2 : asstPerDay,
          only_unvisited: asstUnvisited,
          max_radius: asstRadius || undefined,
          week_start: weekStart,
          ...(asstMaxProposed !== '' ? { max_proposed: Number(asstMaxProposed) } : {}),
          day_hours: Object.fromEntries(asstDays.map(d => [d, getDayHours(d)])),
          ...(wasabiParcMode ? { fixed_slots: FIXED_SLOTS, mode: 'wasabi_parc' } : {}),
          ...(campaign ? { weeks: asstWeeks, interval_weeks: asstInterval } : {}),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      setPlan(d); setShowAssistant(false); setShowPlan(true);
      if (campaign) toast.success(`Campagne générée : ${d.nb_points} points sur ${d.nb_weeks} semaine(s)`);
      else toast.success(`Tournée générée : ${d.nb_points} points sur ${d.days.length} jours`);
    } catch (err) { toast.error(`Échec : ${err.message || ''}`); }
    finally { setAsstRunning(false); }
  };



  const addToRoute = (p) => {
    if (route.find(x => x.id === p.id)) return;
    if (route.length >= 12) { toast.info('Maximum 12 étapes'); return; }
    setRoute(r => [...r, p]); setRouteResult(null);
  };
  const optimize = async () => {
    if (route.length < 2) { toast.info('Ajoutez au moins 2 étapes'); return; }
    setOptimizing(true);
    try {
      const body = { stops: route.map(p => ({ lat: p.lat, lng: p.lng, name: p.name })) };
      if (departure) body.origin = { lat: departure.lat, lng: departure.lng, name: 'Départ' };
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/optimize-route`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setRouteResult(d);
      setRoute(d.ordered_stops.map(s => route.find(p => p.name === s.name) || s));
      toast.success(`Tournée optimisée : ${d.distance_km} km · ${d.duration_min} min`);
    } catch { toast.error("Échec de l'optimisation"); }
    finally { setOptimizing(false); }
  };
  const openInGoogleMaps = () => {
    if (route.length < 2) return;
    const pts = departure ? [departure, ...route] : route;
    const wp = pts.map(p => `${p.lat},${p.lng}`).join('/');
    window.open(`https://www.google.com/maps/dir/${wp}`, '_blank');
  };

  const exportPdf = async () => {
    if (route.length < 1) return;
    setExporting(true);
    try {
      const stops = (departure ? [{ name: `Départ — ${departure.label || ''}`, city: '', address: departure.label }] : []).concat(
        route.map(p => ({ name: p.name, city: p.city, address: p.address, postcode: p.postcode })));
      const body = { stops, legs: routeResult?.legs || [], distance_km: routeResult?.distance_km, duration_min: routeResult?.duration_min };
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/tournee/export-pdf`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'tournee-optimisee.pdf'; a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch { toast.error("Échec de l'export PDF"); }
    finally { setExporting(false); }
  };

  // --- Point de départ ---
  const geocodeRadius = async () => {
    if (!radiusAddr.trim()) return;
    setRadiusLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/geocode`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: radiusAddr }),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setCenter({ lat: d.lat, lng: d.lng, label: radiusAddr });
      if (radius === 0) setRadius(20);
      toast.success('Zone définie autour de ' + radiusAddr);
    } catch { toast.error('Adresse introuvable'); }
    finally { setRadiusLoading(false); }
  };
  const clearRadius = () => { setCenter(null); setRadius(0); setRadiusAddr(''); };
  const launchAssistantOnZone = () => {
    if (!center) return;
    setDeparture({ lat: center.lat, lng: center.lng, label: center.label });
    setDepInput(center.label);
    setAsstRadius(radius || 20);
    setAsstBrands([]);
    setShowAssistant(true);
  };

  const geocodeDeparture = async () => {
    if (!depInput.trim()) return;
    setLocating(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/geocode`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: depInput }),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setDeparture({ lat: d.lat, lng: d.lng, label: depInput });
      setRouteResult(null);
      setFlyTo({ lat: d.lat, lng: d.lng, _t: Date.now() });
      toast.success('Point de départ défini');
    } catch { toast.error('Adresse introuvable'); }
    finally { setLocating(false); }
  };
  const useMyPosition = () => {
    if (!navigator.geolocation) { toast.error('Géolocalisation non disponible'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude, label: 'Ma position' };
        setDeparture(p); setDepInput('Ma position'); setRouteResult(null);
        setFlyTo({ ...p, _t: Date.now() }); setLocating(false);
        toast.success('Position actuelle utilisée comme départ');
      },
      () => { setLocating(false); toast.error("Impossible d'obtenir la position"); },
      { enableHighAccuracy: true, timeout: 8000 });
  };

  // --- Ajout manuel de point ---
  const handleMapClick = useCallback((e) => {
    if (!addMode) return;
    const ll = e.detail?.latLng;
    if (!ll) return;
    setNewPoint({ lat: ll.lat, lng: ll.lng, brand: 'Prospect', name: '', enseigne: '', address: '', postcode: '', city: '', comment: '' });
    setAddMode(false);
  }, [addMode]);

  const saveNewPoint = async () => {
    if (!newPoint) return;
    if (!newPoint.name.trim()) { toast.info('Renseignez un nom'); return; }
    setSavingPoint(true);
    try {
      const meta = brands[newPoint.brand] || {};
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/points`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newPoint, category: meta.category, color: meta.color, icon: meta.icon }),
      });
      if (!r.ok) throw new Error();
      const created = await r.json();
      await loadPoints();
      setNewPoint(null);
      toast.success(`${newPoint.brand} ajouté`);
      if (created?.lat) setFlyTo({ lat: created.lat, lng: created.lng, _t: Date.now() });
    } catch { toast.error("Échec de l'ajout"); }
    finally { setSavingPoint(false); }
  };

  const loadAudit = (type, q) => {
    setAuditLoading(true);
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    if (q) params.set('q', q);
    params.set('limit', '300');
    fetch(`${BACKEND_URL}/api/carte-corners/audit-log?${params}`, { headers: authHeaders })
      .then(r => r.json()).then(setAudit).catch(() => toast.error('Erreur chargement du journal'))
      .finally(() => setAuditLoading(false));
  };
  const openAudit = () => { setShowAudit(true); loadAudit(auditType, auditQ); };

  const loadTournees = () => {
    setTourneesLoading(true);
    fetch(`${BACKEND_URL}/api/carte-corners/tournees`, { headers: authHeaders })
      .then(r => r.json()).then(setTournees).catch(() => toast.error('Erreur chargement des tournées'))
      .finally(() => setTourneesLoading(false));
  };
  const openTournees = () => { setShowTournees(true); loadTournees(); };
  const loadTeam = () => {
    setTeamLoading(true);
    fetch(`${BACKEND_URL}/api/carte-corners/proposals/team`, { headers: authHeaders })
      .then(r => r.json()).then(d => setTeamData(d)).catch(() => toast.error('Erreur de chargement'))
      .finally(() => setTeamLoading(false));
  };
  const openTeam = () => { setShowTeam(true); loadTeam(); };
  const resendTournee = async (id) => {
    setTourneeBusy(id);
    try {
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/tournees/${id}/resend`, { method: 'POST', headers: authHeaders });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || '');
      toast.success(`Tournée renvoyée à ${d.recipient}`);
    } catch (err) { toast.error(`Échec du renvoi : ${err.message || ''}`); }
    finally { setTourneeBusy(''); }
  };
  const deleteTournee = async (id) => {
    if (!window.confirm('Supprimer cette tournée programmée ? Le rappel ne sera plus envoyé.')) return;
    setTourneeBusy(id);
    try {
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/tournees/${id}`, { method: 'DELETE', headers: authHeaders });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || ''); }
      toast.success('Tournée supprimée');
      loadTournees();
    } catch (err) { toast.error(`Échec de la suppression : ${err.message || ''}`); }
    finally { setTourneeBusy(''); }
  };

  const loadHistory = (commercial, from, to) => {
    setHistoryLoading(true);
    const params = new URLSearchParams();
    if (commercial) params.set('commercial', commercial);
    if (from) params.set('date_from', from);
    if (to) params.set('date_to', to);
    fetch(`${BACKEND_URL}/api/carte-corners/tournee-history?${params}`, { headers: authHeaders })
      .then(r => r.json()).then(setHistory).catch(() => toast.error("Erreur chargement de l'historique"))
      .finally(() => setHistoryLoading(false));
  };
  const openHistory = () => { setShowHistory(true); loadHistory(histCommercial, histFrom, histTo); };


  const openAccess = () => {
    setShowAccess(true); setAccessLoading(true);
    fetch(`${BACKEND_URL}/api/carte-corners/access-config`, { headers: authHeaders })
      .then(r => r.json()).then(setAccessCfg).catch(() => toast.error('Erreur chargement des accès'))
      .finally(() => setAccessLoading(false));
  };
  const setUserAccess = async (userId, allowed) => {
    setAccessCfg(cfg => ({ ...cfg, users: cfg.users.map(u => u.id === userId ? { ...u, override: allowed, effective: allowed === null ? (cfg.roles_allowed.includes(u.role) || u.is_admin) : allowed } : u) }));
    try {
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/access-config/user`, {
        method: 'PUT', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, allowed }),
      });
      if (!r.ok) throw new Error();
    } catch { toast.error('Échec de la mise à jour'); openAccess(); }
  };
  const toggleRole = async (role) => {
    const cur = accessCfg.roles_allowed.includes(role);
    const roles = cur ? accessCfg.roles_allowed.filter(r => r !== role) : [...accessCfg.roles_allowed, role];
    try {
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/access-config/roles`, {
        method: 'PUT', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ roles: roles.filter(x => x !== 'admin' && x !== 'super_admin') }),
      });
      if (!r.ok) throw new Error();
      openAccess();
      toast.success('Rôles mis à jour');
    } catch { toast.error('Échec'); }
  };


  const updateBrandStyle = async (brand, patch) => {
    setBrands(prev => ({ ...prev, [brand]: { ...prev[brand], ...patch } }));
    try {
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/brands/${encodeURIComponent(brand)}`, {
        method: 'PUT', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error();
      await loadPoints();
    } catch { toast.error('Échec de la personnalisation'); }
  };
  const applyBulkStyle = async (endpoint, body, label) => {
    setSavingStyle(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/brands/${endpoint}`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      const d = await r.json();
      if (!r.ok) throw new Error();
      setBrands(d.brands || {});
      await loadPoints();
      toast.success(label);
    } catch { toast.error('Échec'); }
    finally { setSavingStyle(false); }
  };


  const reimportPack = async () => {
    if (!window.confirm('Réimporter le pack concurrents (Hana, KellyDeli, Sushiman, Papylee, Katana) ? Les visites et notes existantes sont conservées.')) return;
    setReimporting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/reimport-pack`, { method: 'POST', headers: authHeaders });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      const t = d.total || {};
      toast.success(`Pack réimporté : ${t.crees || 0} créés, ${t.maj || 0} mis à jour (${t.lignes || 0} lignes)`);
      await loadPoints();
    } catch (err) { toast.error(`Réimport échoué : ${err.message || ''}`); }
    finally { setReimporting(false); }
  };


  const doImport = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', f); fd.append('brand', importBrand);
      fd.append('category', (brands[importBrand]?.category) || 'concurrent');
      const r = await fetch(`${BACKEND_URL}/api/carte-corners/import`, { method: 'POST', headers: authHeaders, body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      toast.success(`Import ${importBrand} : ${d.crees} créés, ${d.maj} MAJ, ${d.doublons} doublons`);
      await loadPoints();
    } catch (err) { toast.error(`Import échoué : ${err.message || ''}`); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  if (!GKEY) return <div className="p-8 text-center text-slate-500">Clé Google Maps manquante.</div>;

  const inRoute = (id) => route.some(x => x.id === id);

  return (
    <div className="min-h-[calc(100dvh-1rem)] lg:h-[calc(100vh-1rem)] flex flex-col p-3 gap-3" data-testid="carte-corners-page">
      {/* En-tête : accueil + titre + recherche */}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="carte-home-btn" title="Retour à l'accueil" className="shrink-0">
          <Home className="w-4 h-4" /><span className="ml-1.5">Accueil</span>
        </Button>
        <h1 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2 shrink-0">
          <MapPin className="w-5 h-5 text-orange-500" /> Carte des Corners
        </h1>
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input className="pl-9 pr-16" placeholder="Rechercher (enseigne, ville, CP)…" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && runGeoSearch()} data-testid="carte-search" />
          <Button variant="ghost" size="sm" onClick={runGeoSearch} disabled={geoLoading} data-testid="carte-geo-search-btn" title="Centrer la carte sur cette zone" className="absolute right-1 top-1/2 -translate-y-1/2 h-7 px-2 text-indigo-600 hover:text-indigo-700">
            {geoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="text-xs font-semibold">Zone</span>}
          </Button>
        </div>
        <span className="text-xs font-medium text-slate-400 ml-auto shrink-0">{filtered.length} / {points.length} points</span>
      </div>

      {/* Barre d'outils groupée (desktop) */}
      <div className="hidden lg:flex flex-wrap items-end gap-3">
        {/* Groupe Explorer & terrain */}
        <div className="flex flex-col gap-0.5">
          <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 px-1.5">Explorer</span>
          <div className="flex items-center gap-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-1" data-testid="carte-group-explorer">
            <Button variant="ghost" size="sm" onClick={openDashboard} data-testid="carte-dashboard-btn" title="Tableau de bord terrain">
              <LayoutDashboard className="w-4 h-4" /><span className="ml-1.5 hidden xl:inline">Tableau de bord</span>
            </Button>
            <Button variant={showList ? 'secondary' : 'ghost'} size="sm" onClick={() => setShowList(v => !v)} data-testid="carte-toggle-list" title="Vue liste des points">
              <List className="w-4 h-4" /><span className="ml-1.5 hidden xl:inline">{showList ? 'Masquer la liste' : 'Vue liste'}</span>
            </Button>
            <Button variant={addMode ? 'default' : 'ghost'} size="sm" onClick={() => setAddMode(v => !v)} data-testid="carte-add-mode-btn" title="Ajouter un point sur la carte" className={addMode ? 'bg-green-600 hover:bg-green-700 text-white' : ''}>
              <MapPinned className="w-4 h-4" /><span className="ml-1.5 hidden xl:inline">{addMode ? 'Cliquez sur la carte…' : 'Ajouter un point'}</span>
            </Button>
          </div>
        </div>

        {/* Groupe Données (admin) */}
        {isAdmin && (
          <div className="hidden lg:flex flex-col gap-0.5">
          <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 px-1.5">Données</span>
          <div className="flex items-center gap-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-1" data-testid="carte-group-data">
            <select value={importBrand} onChange={e => setImportBrand(e.target.value)} className="px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs" data-testid="carte-import-brand">
              {Object.keys(brands).filter(b => b !== 'WASABI').map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden onChange={doImport} />
            <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="carte-import-btn" title="Importer un fichier Excel">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}<span className="ml-1.5 hidden xl:inline">Importer</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={reimportPack} disabled={reimporting} data-testid="carte-reimport-pack-btn" title="Recharger les 5 fichiers concurrents (utile après un déploiement)">
              {reimporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Store className="w-4 h-4" />}<span className="ml-1.5 hidden xl:inline">Réimporter</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowBrandCfg(true)} data-testid="carte-brand-cfg-btn" title="Couleur et pictogramme par marque">
              <Palette className="w-4 h-4" /><span className="ml-1.5 hidden xl:inline">Personnaliser</span>
            </Button>
          </div>
          </div>
        )}

        {/* Groupe Gestion tournées & accès (admin) */}
        {isAdmin && (
          <div className="hidden lg:flex flex-col gap-0.5">
          <span className="text-[9px] font-bold uppercase tracking-wider text-indigo-400 px-1.5">Tournées & accès</span>
          <div className="flex items-center gap-1 rounded-xl border border-indigo-200 dark:border-indigo-900/50 bg-indigo-50/40 dark:bg-indigo-900/10 p-1" data-testid="carte-group-manage">
            <Button variant="ghost" size="sm" onClick={openTournees} data-testid="carte-tournees-btn" title="Tournées programmées & rappels automatiques" className="text-indigo-700 dark:text-indigo-300">
              <CalendarClock className="w-4 h-4" /><span className="ml-1.5 hidden xl:inline">Tournées</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={openHistory} data-testid="carte-history-btn" title="Historique d'usage de l'assistant de tournée (par commercial)" className="text-indigo-700 dark:text-indigo-300">
              <ClipboardList className="w-4 h-4" /><span className="ml-1.5 hidden xl:inline">Historique</span>
            </Button>
            {isSuperAdmin && (
              <Button variant="ghost" size="sm" onClick={openAccess} data-testid="carte-access-btn" title="Gérer qui a accès à la carte" className="text-indigo-700 dark:text-indigo-300">
                <ShieldCheck className="w-4 h-4" /><span className="ml-1.5 hidden xl:inline">Accès</span>
              </Button>
            )}
            {isSuperAdmin && (
              <Button variant="ghost" size="sm" onClick={openAudit} data-testid="carte-audit-btn" title="Journal des consultations et modifications" className="text-indigo-700 dark:text-indigo-300">
                <History className="w-4 h-4" /><span className="ml-1.5 hidden xl:inline">Journal</span>
              </Button>
            )}
          </div>
          </div>
        )}
      </div>

      {/* Barre d'actions mobile */}
      <div className="lg:hidden relative">
        <Button variant="outline" size="sm" onClick={() => setMobileMenu(v => !v)} data-testid="carte-mobile-actions-btn" className="w-full justify-center">
          <Menu className="w-4 h-4" /><span className="ml-1.5">Actions</span>
        </Button>
        {mobileMenu && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setMobileMenu(false)} />
            <div className="absolute z-40 mt-1 left-0 right-0 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl p-2 space-y-2 max-h-[70vh] overflow-y-auto" data-testid="carte-mobile-actions-menu">
              <button className="w-full flex items-center justify-center gap-2 text-sm font-semibold px-2 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => { setAsstBrands([]); setShowAssistant(true); setMobileMenu(false); }} data-testid="carte-m-assistant"><Sparkles className="w-4 h-4" /> Assistant tournée</button>
              <div>
                <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 border-l-4 border-slate-300 pl-2 mb-1">Explorer</p>
                <div className="grid grid-cols-1 gap-1">
                  <button className="flex items-center gap-2 text-sm px-2 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-left" onClick={() => { openDashboard(); setMobileMenu(false); }} data-testid="carte-m-dashboard"><LayoutDashboard className="w-4 h-4" /> Tableau de bord</button>
                  <button className="flex items-center gap-2 text-sm px-2 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-left" onClick={() => { setShowList(v => !v); setMobileMenu(false); }} data-testid="carte-m-list"><List className="w-4 h-4" /> {showList ? 'Masquer la liste' : 'Vue liste'}</button>
                  <button className="flex items-center gap-2 text-sm px-2 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-left" onClick={() => { setAddMode(v => !v); setMobileMenu(false); }} data-testid="carte-m-add"><MapPinned className="w-4 h-4" /> {addMode ? 'Annuler l\'ajout' : 'Ajouter un point'}</button>
                </div>
              </div>
              {isAdmin && (
                <div className="pt-1 border-t border-slate-100 dark:border-slate-800">
                  <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 border-l-4 border-emerald-400 pl-2 mb-1">Données</p>
                  <div className="grid grid-cols-1 gap-1">
                    <button className="flex items-center gap-2 text-sm px-2 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-left" onClick={() => { fileRef.current?.click(); setMobileMenu(false); }} data-testid="carte-m-import"><Upload className="w-4 h-4" /> Importer Excel ({importBrand})</button>
                    <button className="flex items-center gap-2 text-sm px-2 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-left" onClick={() => { reimportPack(); setMobileMenu(false); }} data-testid="carte-m-reimport"><Store className="w-4 h-4" /> Réimporter le pack</button>
                    <button className="flex items-center gap-2 text-sm px-2 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-left" onClick={() => { setShowBrandCfg(true); setMobileMenu(false); }} data-testid="carte-m-brandcfg"><Palette className="w-4 h-4" /> Personnaliser</button>
                  </div>
                </div>
              )}
              {isAdmin && (
                <div className="pt-1 border-t border-slate-100 dark:border-slate-800">
                  <p className="text-[9px] font-bold uppercase tracking-wider text-indigo-400 border-l-4 border-indigo-400 pl-2 mb-1">Tournées & accès</p>
                  <div className="grid grid-cols-1 gap-1">
                    <button className="flex items-center gap-2 text-sm px-2 py-2 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 text-left" onClick={() => { openTournees(); setMobileMenu(false); }} data-testid="carte-m-tournees"><CalendarClock className="w-4 h-4" /> Tournées programmées</button>
                    <button className="flex items-center gap-2 text-sm px-2 py-2 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 text-left" onClick={() => { openHistory(); setMobileMenu(false); }} data-testid="carte-m-history"><ClipboardList className="w-4 h-4" /> Historique</button>
                    {isSuperAdmin && <button className="flex items-center gap-2 text-sm px-2 py-2 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 text-left" onClick={() => { openAccess(); setMobileMenu(false); }} data-testid="carte-m-access"><ShieldCheck className="w-4 h-4" /> Accès</button>}
                    {isSuperAdmin && <button className="flex items-center gap-2 text-sm px-2 py-2 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 text-left" onClick={() => { openAudit(); setMobileMenu(false); }} data-testid="carte-m-audit"><History className="w-4 h-4" /> Journal</button>}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Bouton mobile — Filtres & marques */}
      <button type="button" onClick={() => setShowFiltersMobile(v => !v)}
        className="lg:hidden w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm font-medium"
        data-testid="carte-mobile-filters-btn">
        <span className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
          <Filter className="w-4 h-4 text-violet-500" /> Filtres & marques
          {(fRegion || fSecteur || fDept || fActivity || fProposed.size > 0 || center) && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">actifs</span>
          )}
        </span>
        {showFiltersMobile ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[260px_1fr_320px] gap-3 min-h-0">
        {/* Légende / filtres */}
        <Card className={`overflow-y-auto order-3 lg:order-none max-h-[50vh] lg:max-h-none ${showFiltersMobile ? 'block' : 'hidden'} lg:block`}>
          <CardHeader className="pb-2"><CardTitle className="text-sm border-l-4 border-orange-400 pl-2">Marques ({Object.keys(brands).length})</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            <button
              onClick={toggleAllBrands}
              className="w-full text-xs font-semibold px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors mb-1"
              data-testid="carte-brand-toggle-all"
            >
              {allBrandsSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
            {Object.entries(brands).map(([b, meta]) => (
              <label key={b} className="flex items-center gap-2 text-sm cursor-pointer py-1" data-testid={`carte-brand-${b}`}>
                <input type="checkbox" checked={activeBrands?.has(b) || false} onChange={() => toggleBrand(b)} className="w-4 h-4" />
                <img src={iconUrl(meta.color, meta.icon)} alt="" className="w-4 h-4" />
                <span className="flex-1 text-slate-700 dark:text-slate-200">{b}</span>
                <span className="text-xs text-slate-400">{counts[b] || 0}</span>
              </label>
            ))}
            <div className="pt-3 border-t border-slate-100 dark:border-slate-800 mt-2">
              <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 border-l-4 border-blue-400 pl-2">Rayon autour d'une adresse</p>
              <div className="flex gap-1">
                <div className="relative flex-1">
                  <input value={radiusAddr} onChange={e => setRadiusAddr(e.target.value)} onKeyDown={e => e.key === 'Enter' && geocodeRadius()} placeholder="Ville ou adresse…" className="w-full pl-2 pr-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs" data-testid="carte-radius-addr" />
                </div>
                <button onClick={geocodeRadius} disabled={radiusLoading} className="px-2 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white" data-testid="carte-radius-geocode" title="Définir la zone">
                  {radiusLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </button>
              </div>
              {center && (
                <div className="flex items-center justify-between gap-1 mt-1.5 text-[11px] text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 rounded px-2 py-1" data-testid="carte-radius-active">
                  <span className="truncate flex items-center gap-1"><MapPin className="w-3 h-3 shrink-0" /> {center.label}</span>
                  <button onClick={clearRadius} className="text-slate-400 hover:text-red-500 shrink-0" data-testid="carte-radius-clear"><X className="w-3.5 h-3.5" /></button>
                </div>
              )}
              <div className="flex flex-wrap gap-1 mt-1.5">
                {[10, 20, 50, 100].map(r => (
                  <button key={r} onClick={() => setRadius(r)} disabled={!center} className={`text-xs px-2 py-1 rounded-full border disabled:opacity-40 disabled:cursor-not-allowed ${center && radius === r ? 'bg-orange-500 border-orange-500 text-white' : 'border-slate-200 dark:border-slate-700 text-slate-500'}`} data-testid={`carte-radius-${r}`}>
                    {r} km
                  </button>
                ))}
              </div>
              {!center && <p className="text-[11px] text-slate-400 mt-1">Saisissez une adresse pour n'afficher que les corners à proximité.</p>}
              {center && (
                <Button size="sm" className="w-full mt-2 bg-indigo-600 hover:bg-indigo-700" onClick={launchAssistantOnZone} data-testid="carte-radius-launch-assistant">
                  <Sparkles className="w-4 h-4" /><span className="ml-1.5">Lancer l'Assistant sur cette zone</span>
                </Button>
              )}
            </div>
            <div className="pt-3 border-t border-slate-100 dark:border-slate-800 mt-2 space-y-2">
              <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 border-l-4 border-violet-400 pl-2">Filtres avancés</p>
              <select value={fSecteur} onChange={e => setFSecteur(e.target.value)} className="w-full px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs" data-testid="carte-filter-secteur">
                <option value="">Secteur commercial — tous</option>
                {['Nord-Ouest', 'Nord-Est', 'Sud-Ouest', 'Sud-Est'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={fRegion} onChange={e => setFRegion(e.target.value)} className="w-full px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs" data-testid="carte-filter-region">
                <option value="">Région — toutes</option>
                {filterOpts.regions.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <select value={fDept} onChange={e => setFDept(e.target.value)} className="w-full px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs" data-testid="carte-filter-dept">
                <option value="">Département — tous</option>
                {filterOpts.depts.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <select value={fActivity} onChange={e => setFActivity(e.target.value)} className="w-full px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs" data-testid="carte-filter-activity">
                <option value="">Activité — toutes</option>
                <option value="never">Jamais visité</option>
                <option value="visited">Visité</option>
                <option value="lt30">Visité &lt; 30 jours</option>
                <option value="lt90">Visité &lt; 90 jours</option>
                <option value="gt6m">Sans visite &gt; 6 mois</option>
              </select>
              <div>
                <p className="text-[11px] font-medium text-slate-500 mb-1">Proposé par l'outil <span className="text-slate-400">(pour moi)</span></p>
                <div className="flex flex-wrap gap-1" data-testid="carte-filter-proposed">
                  {[['0', 'Jamais'], ['1', '1×'], ['2', '2×'], ['3+', '3×+']].map(([k, l]) => {
                    const on = fProposed.has(k);
                    return (
                      <button key={k} onClick={() => toggleProposed(k)} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? 'bg-violet-600 text-white border-violet-600' : 'border-slate-200 dark:border-slate-700 text-slate-500 hover:border-violet-300'}`} data-testid={`carte-filter-proposed-${k}`}>{l}</button>
                    );
                  })}
                </div>
              </div>
              <div className="rounded-lg border border-slate-100 dark:border-slate-800 p-2 space-y-2" data-testid="carte-freq-view">
                <label className="flex items-center gap-2 text-xs font-medium text-slate-600 dark:text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={freqView} onChange={e => setFreqView(e.target.checked)} className="w-4 h-4" data-testid="carte-freq-toggle" />
                  Colorer par fréquence de proposition
                </label>
                {freqView && (
                  <>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
                      {[['#D9D6CE', 'Jamais'], ['#A6A49E', '1×'], ['#6B6964', '2×'], ['#F26A1B', '3×+']].map(([c, l]) => (
                        <span key={l} className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: c }} /> {l}</span>
                      ))}
                    </div>
                    {isAdmin && (
                      <label className="flex items-center gap-2 text-[11px] text-slate-500 cursor-pointer">
                        <input type="checkbox" checked={freqTeam} onChange={e => setFreqTeam(e.target.checked)} className="w-3.5 h-3.5" data-testid="carte-freq-team-toggle" />
                        Baser la couleur sur le total équipe (au lieu de « pour moi »)
                      </label>
                    )}
                  </>
                )}
                {isAdmin && (
                  <button onClick={openTeam} className="text-[11px] text-indigo-600 hover:underline flex items-center gap-1" data-testid="carte-team-btn">
                    <Users className="w-3 h-3" /> Voir les propositions par équipe
                  </button>
                )}
              </div>
              {(fRegion || fSecteur || fDept || fActivity || fProposed.size > 0) && (
                <button onClick={() => { setFRegion(''); setFSecteur(''); setFDept(''); setFActivity(''); setFProposed(new Set()); }} className="text-[11px] text-orange-600 hover:underline" data-testid="carte-filter-reset">
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Carte */}
        <Card className="overflow-hidden relative order-1 lg:order-none min-h-[55vh] lg:min-h-0">
          <APIProvider apiKey={GKEY}>
            <Map defaultCenter={FRANCE} defaultZoom={6} gestureHandling="greedy" disableDefaultUI={false}
                 mapId="DEMO_MAP_ID" style={{ width: '100%', height: '100%' }}
                 onClick={handleMapClick}
                 data-testid="carte-map">
              {!loading && <MarkersLayer points={filtered} onSelect={selectPoint} freqView={freqView} countKey={(freqTeam && isAdmin) ? 'team_proposed_count' : 'my_proposed_count'} />}
              <MapController flyTo={flyTo} departure={departure} searchMarker={geoSearch} />
              <RadiusCircle center={center} radiusKm={radius} />
            </Map>
          </APIProvider>
          {loading && <div className="absolute inset-0 flex items-center justify-center bg-white/60"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>}
          {addMode && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-green-600 text-white text-xs px-3 py-1.5 rounded-full shadow-lg z-10">
              Cliquez sur la carte pour placer le point
            </div>
          )}
          {center && radius > 0 && !addMode && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-full shadow-lg z-10" data-testid="carte-radius-count">
              <MapPin className="w-4 h-4" />
              <span><b className="tabular-nums">{filtered.length}</b> corner{filtered.length > 1 ? 's' : ''} couvert{filtered.length > 1 ? 's' : ''}</span>
              <span className="text-blue-200 text-xs">· {radius} km autour de {center.label}</span>
            </div>
          )}
          {/* Vue liste superposée */}
          {showList && (
            <div className="absolute top-2 left-2 bottom-2 w-72 bg-white/95 dark:bg-slate-900/95 backdrop-blur rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 flex flex-col z-10" data-testid="carte-list-panel">
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 dark:border-slate-800">
                <span className="text-xs font-semibold text-slate-600 dark:text-slate-200">Points ({filtered.length})</span>
                <button onClick={() => setShowList(false)} className="text-slate-400"><X className="w-4 h-4" /></button>
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
                {filtered.slice(0, 400).map((p) => (
                  <button key={p.id} onClick={() => focusPoint(p)} className="w-full text-left px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2" data-testid={`carte-list-row-${p.id}`}>
                    <img src={pointImg(p)} alt="" className="w-4 h-4 shrink-0" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm truncate text-slate-700 dark:text-slate-200">{p.name || p.enseigne || p.city}</span>
                      <span className="block text-[11px] text-slate-400 truncate">{p.brand}{p.city ? ` · ${p.city}` : ''}</span>
                    </span>
                  </button>
                ))}
                {filtered.length > 400 && <div className="px-3 py-2 text-[11px] text-slate-400">Affichage des 400 premiers — affinez la recherche.</div>}
              </div>
            </div>
          )}
        </Card>

        {/* Panneau latéral : fiche + tournée */}
        <div className="flex flex-col gap-3 overflow-y-auto order-2 lg:order-none">
          {geoSearch && geoResults && (
            <Card data-testid="carte-geo-results" className="border-rose-200 dark:border-rose-900">
              <CardHeader className="pb-2 flex flex-row items-start justify-between">
                <div>
                  <CardTitle className="text-sm flex items-center gap-2"><MapPin className="w-4 h-4 text-rose-600" /> Autour de « {geoSearch.label} »</CardTitle>
                  <p className="text-[11px] text-slate-400 mt-0.5">Rayon {geoRadius} km</p>
                </div>
                <button onClick={() => setGeoSearch(null)} className="text-slate-400" data-testid="carte-geo-close"><X className="w-4 h-4" /></button>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex flex-wrap gap-1">
                  {[5, 10, 20, 30, 50, 100].map(r => (
                    <button key={r} onClick={() => setGeoRadius(r)} className={`text-xs px-2 py-1 rounded border ${geoRadius === r ? 'bg-rose-100 border-rose-400 text-rose-700' : 'border-slate-200 dark:border-slate-700 text-slate-500'}`} data-testid={`carte-geo-radius-${r}`}>{r} km</button>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-1.5 text-center">
                  <div className="rounded-lg bg-orange-50 dark:bg-orange-900/20 p-1.5"><div className="text-lg font-bold text-orange-600">{geoResults.wasabi}</div><div className="text-[10px] text-slate-500">WASABI</div></div>
                  <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-1.5"><div className="text-lg font-bold text-red-600">{geoResults.concurrents}</div><div className="text-[10px] text-slate-500">Concurrents</div></div>
                  <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-1.5"><div className="text-lg font-bold text-amber-600">{geoResults.non_visites}</div><div className="text-[10px] text-slate-500">Non visités</div></div>
                </div>
                <Button size="sm" variant="outline" className="w-full" onClick={addProjectAtSearch} data-testid="carte-geo-add-project">
                  <Plus className="w-4 h-4" /><span className="ml-1">Créer un projet signé ici</span>
                </Button>
                <div className="max-h-52 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800 -mx-2">
                  {geoResults.list.length === 0 ? (
                    <p className="text-xs text-slate-400 px-2 py-2">Aucun point dans ce rayon.</p>
                  ) : geoResults.list.slice(0, 100).map(p => (
                    <button key={p.id} onClick={() => focusPoint(p)} className="w-full text-left px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2" data-testid={`carte-geo-row-${p.id}`}>
                      <img src={pointImg(p)} alt="" className="w-3.5 h-3.5 shrink-0" />
                      <span className="min-w-0 flex-1 truncate text-xs text-slate-700 dark:text-slate-200">{p.name || p.enseigne || p.city}</span>
                      <span className="text-[11px] text-slate-400 shrink-0">{p._dist.toFixed(1)} km</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {selected ? (
            <Card data-testid="carte-detail">
              <CardHeader className="pb-2 flex flex-row items-start justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <img src={pointImg(selected)} alt="" className="w-5 h-5" />{selected.name || selected.enseigne}
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-0.5">{selected.brand}{selected.enseigne && selected.enseigne !== selected.name ? ` · ${selected.enseigne}` : ''}</p>
                </div>
                <button onClick={() => setSelected(null)} className="text-slate-400"><X className="w-4 h-4" /></button>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="text-slate-600 dark:text-slate-300">{[selected.address, selected.postcode, selected.city].filter(Boolean).join(', ')}</p>
                {/* Coordonnées & métadonnées */}
                <div className="text-xs text-slate-500 space-y-0.5">
                  {selected.phone && <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> {selected.phone}</div>}
                  {selected.email && <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" /> {selected.email}</div>}
                  {selected.website && <div className="flex items-center gap-1.5"><Globe className="w-3 h-3" /> <a href={selected.website} target="_blank" rel="noreferrer" className="text-orange-600 hover:underline truncate">{selected.website}</a></div>}
                  {selected.secteur && <div>Secteur : <b>{selected.secteur}</b>{selected.dept ? ` · ${selected.dept}` : ''}{selected.region ? ` · ${selected.region}` : ''}</div>}
                  {selected.source && <div>Source : {selected.source}</div>}
                  {selected.commercial && <div>Commercial : <b>{selected.commercial}</b></div>}
                  <div>Dernière visite : {selected.last_visit ? new Date(selected.last_visit).toLocaleDateString('fr-FR') : 'jamais'}{selected.visit_count ? ` (${selected.visit_count})` : ''}</div>
                  <div data-testid="carte-detail-proposed">Proposé par l'outil : <b>{selected.my_proposed_count || 0}</b> fois{isAdmin ? <span className="text-slate-400"> · équipe : {selected.team_proposed_count || 0}</span> : null}</div>
                </div>
                {selected.category === 'wasabi' && (
                  <div className="text-xs text-slate-500 space-y-0.5 bg-orange-50 dark:bg-orange-900/20 rounded p-2">
                    {selected.nom_dr && <div>DR : {selected.nom_dr}</div>}
                    {selected.nom_bm && <div>BM : {selected.nom_bm}</div>}
                    {selected.ca_n != null && <div>CA HT N : {Math.round(selected.ca_n).toLocaleString('fr-FR')} €</div>}
                    {selected.ca_n1 != null && <div>CA HT N-1 : {Math.round(selected.ca_n1).toLocaleString('fr-FR')} €</div>}
                    {selected.trend != null && <div className="flex items-center gap-1">Tendance : {selected.trend >= 0 ? <TrendingUp className="w-3 h-3 text-green-600" /> : <TrendingDown className="w-3 h-3 text-red-500" />} {Math.round(selected.trend * 100)}%</div>}
                  </div>
                )}
                {/* Actions rapides */}
                <div className="grid grid-cols-2 gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => openItinerary(selected)} data-testid="carte-itineraire"><Navigation2 className="w-3.5 h-3.5" /><span className="ml-1">Itinéraire</span></Button>
                  <Button size="sm" variant="outline" onClick={() => copyAddress(selected)} data-testid="carte-copy-address"><Copy className="w-3.5 h-3.5" /><span className="ml-1">Copier</span></Button>
                </div>
                {/* Affecter à un commercial */}
                <div className="flex items-center gap-1.5">
                  <UserPlus className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <select value={selected.commercial_id || ''} onChange={e => assignCommercial(e.target.value)} disabled={assigning} className="flex-1 px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs" data-testid="carte-assign-select">
                    <option value="">— Affecter à un commercial —</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                  <p className="text-xs font-semibold text-slate-600 mb-1">Nouvelle visite</p>
                  <textarea value={visitNote} onChange={e => setVisitNote(e.target.value)} rows={2} placeholder="Note de visite…" className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900" data-testid="carte-visit-note" />
                  <Button size="sm" className="w-full mt-1 bg-orange-500 hover:bg-orange-600" onClick={saveVisit} data-testid="carte-visit-save"><Check className="w-4 h-4" /><span className="ml-1">Enregistrer la visite</span></Button>
                </div>
                <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                  <p className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1"><StickyNote className="w-3.5 h-3.5" /> Ajouter une note</p>
                  <textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={2} placeholder="Note libre (hors visite)…" className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900" data-testid="carte-note-text" />
                  <Button size="sm" variant="outline" className="w-full mt-1" onClick={saveNote} data-testid="carte-note-save"><Plus className="w-4 h-4" /><span className="ml-1">Ajouter la note</span></Button>
                </div>
                {notes.length > 0 && (
                  <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-1.5">
                    <p className="text-xs font-semibold text-slate-600">Notes ({notes.length})</p>
                    {notes.map((n, i) => (
                      <div key={i} className="text-xs bg-amber-50 dark:bg-amber-900/20 rounded p-2">
                        <div className="text-slate-400">{new Date(n.date).toLocaleDateString('fr-FR')} · {n.auteur}</div>
                        <div className="text-slate-600 dark:text-slate-300">{n.text}</div>
                      </div>
                    ))}
                  </div>
                )}
                {visits.length > 0 && (
                  <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-1.5">
                    <p className="text-xs font-semibold text-slate-600">Historique visites ({visits.length})</p>
                    {visits.map((v, i) => (
                      <div key={i} className="text-xs bg-slate-50 dark:bg-slate-800 rounded p-2">
                        <div className="text-slate-400">{new Date(v.date).toLocaleDateString('fr-FR')} · {v.commercial}</div>
                        <div className="text-slate-600 dark:text-slate-300">{v.note}</div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card><CardContent className="py-6 text-center text-sm text-slate-400"><Store className="w-6 h-6 mx-auto mb-2 opacity-50" />Cliquez sur un point pour voir sa fiche.</CardContent></Card>
          )}

          {/* Assistant de tournée IA — centre névralgique de la colonne */}
          <Card className="mt-auto border-2 border-orange-200 dark:border-orange-900/50 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/20 shadow-md" data-testid="carte-ai-panel">
            <CardContent className="p-4 text-center space-y-3">
              <div className="flex items-center justify-center gap-2 select-none" data-testid="carte-success-title">
                <Star className="w-5 h-5 text-orange-400 fill-orange-400 shrink-0 animate-pulse" />
                <span className="text-lg font-extrabold uppercase tracking-wide text-orange-500">Votre succès ici !</span>
                <Star className="w-5 h-5 text-orange-400 fill-orange-400 shrink-0 animate-pulse" style={{ animationDelay: '0.6s' }} />
              </div>
              <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-lg shadow-orange-500/30">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">Assistant de tournée IA</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">Générez en un clic une tournée <b>optimisée</b> : trajets réels, horaires d'arrivée, pause déjeuner et export Outlook / Waze.</p>
              </div>
              <Button onClick={() => { setAsstBrands([]); setShowAssistant(true); }} data-testid="carte-assistant-btn" className="w-full min-h-[52px] text-base font-semibold rounded-xl bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white shadow-lg shadow-orange-500/25">
                <Sparkles className="w-5 h-5" /><span className="ml-2">Générer une tournée · IA</span>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modale d'ajout de point */}
      {newPoint && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="carte-add-modal" onClick={() => !savingPoint && setNewPoint(null)}>
          <Card className="w-full max-w-md" onClick={e => e.stopPropagation()}>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><MapPinned className="w-4 h-4 text-green-600" /> Nouveau point</CardTitle>
              <button onClick={() => setNewPoint(null)} className="text-slate-400"><X className="w-4 h-4" /></button>
            </CardHeader>
            <CardContent className="space-y-2.5">
              <div>
                <label className="text-xs font-medium text-slate-500">Type</label>
                <select value={newPoint.brand} onChange={e => setNewPoint({ ...newPoint, brand: e.target.value })} className="w-full mt-0.5 px-2 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm" data-testid="carte-new-brand">
                  {Object.keys(brands).filter(b => b !== 'WASABI').map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Nom *</label>
                <Input value={newPoint.name} onChange={e => setNewPoint({ ...newPoint, name: e.target.value })} placeholder="Nom du point" data-testid="carte-new-name" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-slate-500">Enseigne</label>
                  <Input value={newPoint.enseigne} onChange={e => setNewPoint({ ...newPoint, enseigne: e.target.value })} data-testid="carte-new-enseigne" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Ville</label>
                  <Input value={newPoint.city} onChange={e => setNewPoint({ ...newPoint, city: e.target.value })} data-testid="carte-new-city" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Adresse</label>
                <Input value={newPoint.address} onChange={e => setNewPoint({ ...newPoint, address: e.target.value })} data-testid="carte-new-address" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Commentaire</label>
                <textarea value={newPoint.comment} onChange={e => setNewPoint({ ...newPoint, comment: e.target.value })} rows={2} className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900" data-testid="carte-new-comment" />
              </div>
              <p className="text-[11px] text-slate-400">Position : {newPoint.lat.toFixed(4)}, {newPoint.lng.toFixed(4)}</p>
              <Button className="w-full bg-green-600 hover:bg-green-700" onClick={saveNewPoint} disabled={savingPoint} data-testid="carte-new-save">
                {savingPoint ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}<span className="ml-1">Enregistrer le point</span>
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modale Tableau de bord */}
      {showDash && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="carte-dashboard-modal" onClick={() => setShowDash(false)}>
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardHeader className="pb-2 flex flex-row items-center justify-between sticky top-0 bg-white dark:bg-slate-900 z-10">
              <CardTitle className="text-base flex items-center gap-2"><LayoutDashboard className="w-5 h-5 text-indigo-600" /> Tableau de bord terrain</CardTitle>
              <button onClick={() => setShowDash(false)} className="text-slate-400" data-testid="carte-dashboard-close"><X className="w-4 h-4" /></button>
            </CardHeader>
            <CardContent>
              {dashLoading || !dash ? (
                <div className="py-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { l: 'Corners WASABI', v: dash.total_wasabi, c: 'text-orange-600' },
                      { l: 'Corners concurrents', v: dash.total_concurrents, c: 'text-red-600' },
                      { l: 'Prospects', v: dash.total_prospects, c: 'text-green-600' },
                      { l: 'Projets signés', v: dash.total_projets, c: 'text-blue-600' },
                    ].map(k => (
                      <div key={k.l} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                        <div className={`text-2xl font-bold ${k.c}`}>{k.v}</div>
                        <div className="text-xs text-slate-500">{k.l}</div>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { l: 'Concurrents visités', v: dash.concurrents_visites, c: 'text-emerald-600' },
                      { l: 'Jamais visités', v: dash.concurrents_jamais_visites, c: 'text-amber-600' },
                      { l: 'Taux de couverture', v: `${dash.taux_couverture_pct}%`, c: 'text-indigo-600' },
                      { l: 'Relances en retard (>6 mois)', v: dash.relances_en_retard, c: 'text-rose-600' },
                    ].map(k => (
                      <div key={k.l} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                        <div className={`text-2xl font-bold ${k.c}`}>{k.v}</div>
                        <div className="text-xs text-slate-500">{k.l}</div>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                      <p className="text-sm font-semibold text-slate-600 mb-2">Répartition par concurrent</p>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={dash.par_concurrent} layout="vertical" margin={{ left: 20 }}>
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis type="category" dataKey="marque" tick={{ fontSize: 11 }} width={80} />
                          <RTooltip />
                          <Bar dataKey="n" fill="#25272B" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                      <p className="text-sm font-semibold text-slate-600 mb-2">Évolution des visites (6 mois)</p>
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={dash.trend}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                          <RTooltip />
                          <Line type="monotone" dataKey="visites" stroke="#F26A1B" strokeWidth={2} dot={{ r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                    <p className="text-sm font-semibold text-slate-600 mb-2">Visites du mois par commercial ({dash.visites_mois} au total)</p>
                    {dash.visites_par_commercial.length === 0 ? (
                      <p className="text-xs text-slate-400">Aucune visite ce mois-ci.</p>
                    ) : (
                      <div className="space-y-1">
                        {dash.visites_par_commercial.map(c => (
                          <div key={c.commercial} className="flex items-center justify-between text-sm">
                            <span className="text-slate-600 dark:text-slate-300">{c.commercial}</span>
                            <span className="font-semibold text-indigo-600">{c.n}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      {/* Modale Plan multi-jours */}
      {showPlan && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="carte-plan-modal" onClick={() => setShowPlan(false)}>
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardHeader className="pb-2 flex flex-row items-center justify-between sticky top-0 bg-white dark:bg-slate-900 z-10">
              <CardTitle className="text-base flex items-center gap-2"><Route className="w-5 h-5 text-indigo-600" /> Plan de tournée{plan ? ` · ${plan.total_distance_km} km · ${Math.floor(plan.total_duration_min / 60)}h${String(plan.total_duration_min % 60).padStart(2, '0')}` : ''}</CardTitle>
              <div className="flex items-center gap-2">
                {plan && <Button size="sm" variant="outline" onClick={exportPlanPdf} data-testid="carte-plan-export"><FileDown className="w-4 h-4" /><span className="ml-1">PDF</span></Button>}
                {plan && <Button size="sm" variant="outline" onClick={exportPlanExcel} data-testid="carte-plan-excel"><FileDown className="w-4 h-4" /><span className="ml-1">Excel</span></Button>}
                {plan && <Button size="sm" variant="outline" onClick={exportPlanIcs} data-testid="carte-plan-ics"><FileDown className="w-4 h-4" /><span className="ml-1">Outlook</span></Button>}
                <button onClick={() => setShowPlan(false)} className="text-slate-400" data-testid="carte-plan-close"><X className="w-4 h-4" /></button>
              </div>
            </CardHeader>
            <CardContent>
              {planLoading || !plan ? (
                <div className="py-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>
              ) : (
                <div className="space-y-3">
                  {departure && <p className="text-xs text-sky-600 flex items-center gap-1"><Flag className="w-3.5 h-3.5" /> Départ/retour : {departure.label}</p>}
                  {wasabiParcMode ? (
                    <div className="flex items-center gap-2 text-xs text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/15 border border-orange-200 dark:border-orange-900/40 rounded-lg px-3 py-2">
                      <CalendarDays className="w-4 h-4 shrink-0" />
                      <span>Créneaux fixes (profil {roleLabel}) : <b>9h30</b> le matin · <b>14h</b> l'après-midi.</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-2">
                      <span className="font-medium">Durée par prospection :</span>
                      <select value={asstVisitMin} onChange={e => setAsstVisitMin(+e.target.value)} className="px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900" data-testid="carte-plan-visit-min">
                        {[15, 20, 30, 45, 60, 90].map(m => <option key={m} value={m}>{m} min</option>)}
                      </select>
                      <span className="text-slate-400">— les horaires et trajets ci-dessous se recalculent automatiquement.</span>
                    </div>
                  )}
                  {plan.days.map(day => {
                    const ag = dayAgenda(day);
                    return (
                    <div key={day.jour} className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid={`carte-plan-day-${day.jour}`}>
                      <div className="flex items-center justify-between bg-indigo-50 dark:bg-indigo-900/20 px-3 py-2">
                        <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">{day.jour_label || `Jour ${day.jour}`}{day.date ? ` · ${new Date(day.date).toLocaleDateString('fr-FR')}` : ''}{!wasabiParcMode && day.start_hour != null ? ` · ${day.start_hour}h–${day.end_hour}h` : ''} · {day.stops.length} visite{day.stops.length > 1 ? 's' : ''}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">{day.distance_km} km · {Math.floor(day.duration_min / 60)}h{String(day.duration_min % 60).padStart(2, '0')} de route</span>
                          {ag && <span className={`text-[11px] px-1.5 py-0.5 rounded ${ag.over ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`} title="Fin estimée avec trajets réels + temps de prospection">fin ~{fmtHm(ag.end)}{ag.over ? ' ⚠' : ''}</span>}
                          <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => openDayInMaps(day)} data-testid={`carte-plan-gmaps-${day.jour}`}><Navigation2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                      <ol className="divide-y divide-slate-100 dark:divide-slate-800">
                        {day.stops.map((s, i) => {
                          const leg = (day.legs || [])[i];
                          const row = ag?.rows[i];
                          return (
                          <React.Fragment key={i}>
                            {leg && (leg.duration_min != null) && (
                              <li className="flex items-center gap-2 px-3 py-1 bg-slate-50/60 dark:bg-slate-800/40">
                                <Navigation2 className="w-3.5 h-3.5 text-slate-400 shrink-0 ml-1" />
                                <span className="text-[11px] text-slate-500">{i === 0 ? 'Trajet depuis le départ' : 'Trajet'} : <b className="text-slate-600 dark:text-slate-300">{leg.duration_min} min</b>{leg.distance_km != null ? ` · ${leg.distance_km} km` : ''}</span>
                              </li>
                            )}
                            {row?.lunchBefore && (
                              <li className="flex items-center gap-2 px-3 py-1 bg-amber-50 dark:bg-amber-900/20">
                                <span className="text-[11px] text-amber-700 dark:text-amber-300 ml-1">🍽️ Pause déjeuner ({asstLunchMin} min)</span>
                              </li>
                            )}
                            <li className="flex items-center gap-2 px-3 py-1.5 text-sm">
                              <span className="w-5 h-5 rounded-full bg-indigo-600 text-white text-[11px] flex items-center justify-center shrink-0">{i + 1}</span>
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-slate-700 dark:text-slate-200">{s.name || s.city}</span>
                                {(s.address || s.city) && <span className="block text-[11px] text-slate-400 truncate">{[s.address, s.postcode, s.city].filter(Boolean).join(', ')}</span>}
                              </span>
                              {row && <span className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-300 shrink-0 tabular-nums" title="Heure d'arrivée estimée">{fmtHm(row.arrive)}</span>}
                            </li>
                          </React.Fragment>
                          );
                        })}
                      </ol>
                    </div>
                    );
                  })}
                  {plan.non_planifies?.length > 0 && (
                    <div className="rounded-xl border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-900/20 p-3">
                      <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-1">Non planifiés ({plan.non_planifies.length}) — dépassent la capacité {planDays}×{planPerDay}</p>
                      <div className="text-xs text-slate-500">{plan.non_planifies.map(s => s.name || s.city).join(', ')}</div>
                    </div>
                  )}
                  <div className="rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-900/20 p-3 space-y-2" data-testid="carte-plan-email-block">
                    <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5"><Mail className="w-4 h-4" /> Envoyer à un utilisateur (PDF + agenda Outlook)</p>
                    <div className="flex flex-wrap items-center gap-2">
                      <select value={emailUserId} onChange={e => { const id = e.target.value; setEmailUserId(id); const u = users.find(x => x.id === id); setEmailTo(u?.email || ''); }} className="flex-1 min-w-[160px] px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs" data-testid="carte-plan-email-user">
                        <option value="">— Choisir un utilisateur —</option>
                        {users.filter(u => u.email).map(u => <option key={u.id} value={u.id}>{u.name}{u.role ? ` · ${u.role}` : ''}</option>)}
                      </select>
                      <span className="text-xs text-slate-400">ou</span>
                      <Input value={emailTo} onChange={e => { setEmailTo(e.target.value); if (e.target.value) setEmailUserId(''); }} placeholder="email@exemple.fr" className="flex-1 min-w-[160px] h-8 text-xs" data-testid="carte-plan-email-input" />
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={sendPlanEmail} disabled={sendingEmail} data-testid="carte-plan-email-send">
                        {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}<span className="ml-1">Envoyer</span>
                      </Button>
                    </div>
                  </div>

                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      {/* Modale Personnalisation des marques */}
      {showBrandCfg && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="carte-brand-cfg-modal" onClick={() => setShowBrandCfg(false)}>
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardHeader className="pb-2 flex flex-row items-center justify-between sticky top-0 bg-white dark:bg-slate-900 z-10">
              <CardTitle className="text-base flex items-center gap-2"><Palette className="w-5 h-5 text-indigo-600" /> Couleur & pictogramme par marque</CardTitle>
              <button onClick={() => setShowBrandCfg(false)} className="text-slate-400" data-testid="carte-brand-cfg-close"><X className="w-4 h-4" /></button>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => applyBulkStyle('preset-distinct', {}, 'Palette de points colorés appliquée')} disabled={savingStyle} data-testid="carte-preset-distinct">
                  {savingStyle ? <Loader2 className="w-4 h-4 animate-spin" /> : <Palette className="w-4 h-4" />}<span className="ml-1">Points de couleurs distinctes</span>
                </Button>
                <Button size="sm" variant="outline" onClick={() => applyBulkStyle('bulk-icon', { icon: 'circle' }, 'Toutes les marques en ronds')} disabled={savingStyle} data-testid="carte-bulk-circle">
                  <span className="w-3 h-3 rounded-full bg-slate-500 inline-block mr-1.5" />Tout en ronds
                </Button>
              </div>
              <div className="space-y-2">
                {Object.entries(brands).map(([b, meta]) => (
                  <div key={b} className="flex items-center gap-3 p-2 rounded-lg border border-slate-100 dark:border-slate-800" data-testid={`carte-brand-row-${b}`}>
                    <img src={iconUrl(meta.color, meta.icon)} alt="" className="w-6 h-6" />
                    <span className="flex-1 text-sm text-slate-700 dark:text-slate-200">{b}</span>
                    <input type="color" value={meta.color || '#dc2626'} onChange={e => updateBrandStyle(b, { color: e.target.value })} className="w-9 h-9 rounded cursor-pointer border border-slate-200 dark:border-slate-700 bg-transparent" data-testid={`carte-brand-color-${b}`} title="Couleur" />
                    <select value={meta.icon || 'circle'} onChange={e => updateBrandStyle(b, { icon: e.target.value })} className="px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm" data-testid={`carte-brand-icon-${b}`}>
                      <option value="circle">● Rond</option>
                      <option value="triangle">▲ Triangle</option>
                      <option value="diamond">◆ Losange</option>
                      <option value="star">★ Étoile</option>
                    </select>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-400">Les changements s'appliquent immédiatement à tous les points de la carte.</p>
            </CardContent>
          </Card>
        </div>
      )}
      {/* Modale Gestion des accès (Super Admin) */}
      {showAccess && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="carte-access-modal" onClick={() => setShowAccess(false)}>
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardHeader className="pb-2 flex flex-row items-center justify-between sticky top-0 bg-white dark:bg-slate-900 z-10">
              <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-600" /> Accès à la Carte des Corners</CardTitle>
              <button onClick={() => setShowAccess(false)} className="text-slate-400" data-testid="carte-access-close"><X className="w-4 h-4" /></button>
            </CardHeader>
            <CardContent className="space-y-4">
              {accessLoading || !accessCfg ? (
                <div className="py-10 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
              ) : (
                <>
                  <div>
                    <p className="text-sm font-semibold text-slate-600 mb-2">Rôles autorisés par défaut</p>
                    <div className="flex flex-wrap gap-2">
                      {accessCfg.roles_present.map(role => {
                        const isAdminRole = role === 'admin' || role === 'super_admin';
                        const on = isAdminRole || accessCfg.roles_allowed.includes(role);
                        return (
                          <button key={role} disabled={isAdminRole} onClick={() => !isAdminRole && toggleRole(role)}
                            className={`text-xs px-3 py-1.5 rounded-full border ${on ? 'bg-emerald-100 border-emerald-400 text-emerald-700' : 'border-slate-200 dark:border-slate-700 text-slate-500'} ${isAdminRole ? 'opacity-60 cursor-not-allowed' : ''}`}
                            data-testid={`carte-access-role-${role}`}>
                            {accessCfg.role_labels[role] || role}{isAdminRole ? ' (toujours)' : ''}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">Les administrateurs ont toujours accès. Les exceptions par personne sont réglées ci-dessous.</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-600 mb-2">Par utilisateur ({accessCfg.users.length})</p>
                    <div className="rounded-xl border border-slate-100 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
                      {accessCfg.users.map(u => (
                        <div key={u.id} className="flex items-center gap-3 px-3 py-2" data-testid={`carte-access-user-${u.id}`}>
                          <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${u.effective ? 'bg-emerald-500' : 'bg-red-400'}`} />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm text-slate-700 dark:text-slate-200 truncate">{u.name}</span>
                            <span className="block text-[11px] text-slate-400 truncate">{u.email} · {u.role_label}</span>
                          </span>
                          {u.is_admin ? (
                            <span className="text-xs text-slate-400">Admin (accès total)</span>
                          ) : (
                            <select value={u.override === null || u.override === undefined ? 'default' : (u.override ? 'allow' : 'deny')}
                              onChange={e => { const v = e.target.value; setUserAccess(u.id, v === 'default' ? null : v === 'allow'); }}
                              className="px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs" data-testid={`carte-access-select-${u.id}`}>
                              <option value="default">Par rôle {u.override === null ? (accessCfg.roles_allowed.includes(u.role) ? '(autorisé)' : '(refusé)') : ''}</option>
                              <option value="allow">Autorisé</option>
                              <option value="deny">Refusé</option>
                            </select>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      {/* Modale Journal des accès (Super Admin) */}
      {showAudit && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="carte-audit-modal" onClick={() => setShowAudit(false)}>
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardHeader className="pb-2 flex flex-row items-center justify-between sticky top-0 bg-white dark:bg-slate-900 z-10">
              <CardTitle className="text-base flex items-center gap-2"><History className="w-5 h-5 text-slate-600" /> Journal des accès</CardTitle>
              <button onClick={() => setShowAudit(false)} className="text-slate-400" data-testid="carte-audit-close"><X className="w-4 h-4" /></button>
            </CardHeader>
            <CardContent className="space-y-3">
              {audit && (
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-2"><div className="text-lg font-bold text-slate-700 dark:text-slate-200">{audit.total}</div><div className="text-[10px] text-slate-500">Total</div></div>
                  <div className="rounded-lg bg-sky-50 dark:bg-sky-900/20 p-2"><div className="text-lg font-bold text-sky-600">{audit.views}</div><div className="text-[10px] text-slate-500">Consultations</div></div>
                  <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-2"><div className="text-lg font-bold text-amber-600">{audit.edits}</div><div className="text-[10px] text-slate-500">Modifications</div></div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <select value={auditType} onChange={e => { setAuditType(e.target.value); loadAudit(e.target.value, auditQ); }} className="px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs" data-testid="carte-audit-type">
                  <option value="">Tout</option>
                  <option value="view">Consultations</option>
                  <option value="edit">Modifications</option>
                </select>
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input value={auditQ} onChange={e => setAuditQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadAudit(auditType, auditQ)} placeholder="Filtrer par utilisateur ou action…" className="pl-8 h-8 text-xs" data-testid="carte-audit-search" />
                </div>
              </div>
              {auditLoading || !audit ? (
                <div className="py-10 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
              ) : audit.entries.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">Aucune entrée.</p>
              ) : (
                <div className="rounded-xl border border-slate-100 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
                  {audit.entries.map((e, i) => (
                    <div key={i} className="flex items-start gap-3 px-3 py-2" data-testid="carte-audit-row">
                      <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${e.type === 'view' ? 'bg-sky-500' : 'bg-amber-500'}`} />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-slate-700 dark:text-slate-200">{e.action}{e.detail ? <span className="text-slate-400"> — {e.detail}</span> : ''}</span>
                        <span className="block text-[11px] text-slate-400">{e.user_name} · {new Date(e.ts).toLocaleString('fr-FR')}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      {/* Modale Tournées programmées & rappels (Admin) */}
      {showTournees && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="carte-tournees-modal" onClick={() => setShowTournees(false)}>
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardHeader className="pb-2 flex flex-row items-center justify-between sticky top-0 bg-white dark:bg-slate-900 z-10">
              <CardTitle className="text-base flex items-center gap-2"><CalendarClock className="w-5 h-5 text-indigo-600" /> Tournées programmées</CardTitle>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="h-8" onClick={loadTournees} data-testid="carte-tournees-refresh">Actualiser</Button>
                <button onClick={() => setShowTournees(false)} className="text-slate-400" data-testid="carte-tournees-close"><X className="w-4 h-4" /></button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-[11px] text-slate-500 flex items-center gap-1"><BellRing className="w-3.5 h-3.5 text-indigo-500" /> Un rappel est envoyé automatiquement au commercial à 18h la veille du 1er jour de tournée.</p>
              {tournees && (
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-2"><div className="text-lg font-bold text-slate-700 dark:text-slate-200">{tournees.total}</div><div className="text-[10px] text-slate-500">Total</div></div>
                  <div className="rounded-lg bg-indigo-50 dark:bg-indigo-900/20 p-2"><div className="text-lg font-bold text-indigo-600">{tournees.upcoming}</div><div className="text-[10px] text-slate-500">À venir</div></div>
                  <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 p-2"><div className="text-lg font-bold text-emerald-600">{tournees.reminded}</div><div className="text-[10px] text-slate-500">Rappels envoyés</div></div>
                </div>
              )}
              {tourneesLoading || !tournees ? (
                <div className="py-10 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
              ) : tournees.tournees.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">Aucune tournée programmée. Envoyez une tournée à un commercial depuis l'Assistant pour la voir ici.</p>
              ) : (
                <div className="space-y-2">
                  {tournees.tournees.map((t) => (
                    <div key={t.id} className="rounded-xl border border-slate-100 dark:border-slate-800 p-3 flex items-start gap-3" data-testid="carte-tournee-row">
                      <span className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${t.reminder_sent ? 'bg-emerald-500' : t.is_upcoming ? 'bg-indigo-500' : 'bg-slate-300'}`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{t.recipient_name || t.recipient_email}</div>
                        <div className="text-[11px] text-slate-500">{t.recipient_email}</div>
                        <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                          Départ le <b>{t.first_date ? new Date(t.first_date).toLocaleDateString('fr-FR') : '—'}</b> · {t.nb_days} jour(s) · {t.nb_points} visites · {t.total_distance_km} km
                        </div>
                        <div className="text-[11px] mt-1">
                          {t.reminder_sent
                            ? <span className="text-emerald-600">✓ Rappel envoyé{t.reminder_sent_at ? ` le ${new Date(t.reminder_sent_at).toLocaleString('fr-FR')}` : ''}</span>
                            : <span className="text-indigo-600">⏰ Rappel prévu le {t.reminder_date ? new Date(t.reminder_date).toLocaleDateString('fr-FR') : '—'} à 18h</span>}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5 shrink-0">
                        <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => resendTournee(t.id)} disabled={tourneeBusy === t.id} data-testid={`carte-tournee-resend-${t.id}`} title="Renvoyer le plan par email maintenant">
                          {tourneeBusy === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        </Button>
                        {isAdmin && (
                          <Button size="sm" variant="outline" className="h-8 px-2 text-red-500 hover:text-red-600" onClick={() => deleteTournee(t.id)} disabled={tourneeBusy === t.id} data-testid={`carte-tournee-delete-${t.id}`} title="Supprimer">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      {/* Modale Historique d'usage (Admin/Super Admin) */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="carte-history-modal" onClick={() => setShowHistory(false)}>
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardHeader className="pb-2 flex flex-row items-center justify-between sticky top-0 bg-white dark:bg-slate-900 z-10">
              <CardTitle className="text-base flex items-center gap-2"><History className="w-5 h-5 text-indigo-600" /> Historique des tournées envoyées</CardTitle>
              <button onClick={() => setShowHistory(false)} className="text-slate-400" data-testid="carte-history-close"><X className="w-4 h-4" /></button>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Mini tableau de bord d'usage */}
              {history && history.monthly && history.monthly.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="carte-history-dashboard">
                  <div className="sm:col-span-2 rounded-lg border border-slate-100 dark:border-slate-800 p-2">
                    <p className="text-[11px] font-semibold text-slate-500 mb-1">Envois par mois</p>
                    <div style={{ height: 120 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={history.monthly.map(m => ({ label: `${m.month.slice(5)}/${m.month.slice(2, 4)}`, count: m.count }))}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={20} />
                          <RTooltip />
                          <Bar dataKey="count" fill="#25272B" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-100 dark:border-slate-800 p-2">
                    <p className="text-[11px] font-semibold text-slate-500 mb-1">Top commerciaux</p>
                    <div className="space-y-1">
                      {history.by_commercial.slice(0, 5).map((c, idx) => (
                        <button key={c.email} onClick={() => { setHistCommercial(c.email); loadHistory(c.email, histFrom, histTo); }} className="w-full flex items-center justify-between text-xs hover:text-indigo-600" data-testid={`carte-history-top-${c.email}`}>
                          <span className="truncate">{idx === 0 ? '🥇 ' : `${idx + 1}. `}{c.name || c.email}</span>
                          <span className="font-bold text-indigo-600 ml-2">{c.count}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {/* Filtres */}
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[180px]">
                  <p className="text-[11px] text-slate-500 mb-1">Commercial (destinataire)</p>
                  <select value={histCommercial} onChange={e => { setHistCommercial(e.target.value); loadHistory(e.target.value, histFrom, histTo); }} className="w-full px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs" data-testid="carte-history-commercial">
                    <option value="">Tous les commerciaux</option>
                    {(history?.commercials || []).map(c => <option key={c.email} value={c.email}>{c.name ? `${c.name} (${c.email})` : c.email}</option>)}
                  </select>
                </div>
                <div>
                  <p className="text-[11px] text-slate-500 mb-1">Du</p>
                  <input type="date" value={histFrom} onChange={e => { setHistFrom(e.target.value); loadHistory(histCommercial, e.target.value, histTo); }} className="px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs" data-testid="carte-history-from" />
                </div>
                <div>
                  <p className="text-[11px] text-slate-500 mb-1">Au</p>
                  <input type="date" value={histTo} onChange={e => { setHistTo(e.target.value); loadHistory(histCommercial, histFrom, e.target.value); }} className="px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs" data-testid="carte-history-to" />
                </div>
                {(histCommercial || histFrom || histTo) && (
                  <Button size="sm" variant="outline" className="h-8" onClick={() => { setHistCommercial(''); setHistFrom(''); setHistTo(''); loadHistory('', '', ''); }} data-testid="carte-history-reset">Réinitialiser</Button>
                )}
              </div>
              {/* Compteur par commercial */}
              {history && history.by_commercial.length > 0 && (
                <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-2">
                  <p className="text-[11px] font-semibold text-slate-500 mb-1.5">Tournées envoyées par commercial</p>
                  <div className="flex flex-wrap gap-1.5">
                    {history.by_commercial.map(c => (
                      <button key={c.email} onClick={() => { setHistCommercial(c.email); loadHistory(c.email, histFrom, histTo); }} className={`text-xs px-2.5 py-1 rounded-full border ${histCommercial === c.email ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-indigo-300'}`} data-testid={`carte-history-count-${c.email}`}>
                        {c.name || c.email} <span className="font-bold">{c.count}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-xs text-slate-400">{history ? `${history.total} envoi(s)` : ''}{history && history.shown < history.total ? ` · ${history.shown} affichés` : ''}</p>
              {/* Liste */}
              {historyLoading || !history ? (
                <div className="py-10 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
              ) : history.envois.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">Aucun envoi pour ces filtres.</p>
              ) : (
                <div className="rounded-xl border border-slate-100 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
                  {history.envois.map((e, i) => (
                    <div key={i} className="flex items-start gap-3 px-3 py-2" data-testid="carte-history-row">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 mt-0.5 ${e.kind === 'renvoi' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'}`}>{e.kind === 'renvoi' ? 'RENVOI' : 'ENVOI'}</span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-700 dark:text-slate-200">
                          <b>{e.recipient_name || e.recipient_email}</b>
                          {e.is_campaign && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">Campagne{e.nb_weeks ? ` · ${e.nb_weeks} sem.` : ''}</span>}
                        </div>
                        <div className="text-[11px] text-slate-400">{e.recipient_email} · {e.nb_points} visites · {e.total_distance_km} km{e.first_date ? ` · départ ${new Date(e.first_date).toLocaleDateString('fr-FR')}` : ''}</div>
                        <div className="text-[11px] text-slate-400">Envoyé par {e.sent_by_name || e.sent_by} · {new Date(e.sent_at).toLocaleString('fr-FR')}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {showTeam && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="carte-team-modal" onClick={() => setShowTeam(false)}>
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardHeader className="pb-2 flex flex-row items-center justify-between sticky top-0 bg-white dark:bg-slate-900 z-10">
              <CardTitle className="text-base flex items-center gap-2"><Users className="w-5 h-5 text-indigo-600" /> Propositions par équipe</CardTitle>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="h-8" onClick={loadTeam} data-testid="carte-team-refresh">Actualiser</Button>
                <button onClick={() => setShowTeam(false)} className="text-slate-400" data-testid="carte-team-close"><X className="w-4 h-4" /></button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-slate-500">Nombre de fois où chaque corner a été proposé par l'outil, <b>tous commerciaux confondus</b> (comptabilisé à l'envoi d'une tournée).</p>
              <Input value={teamQ} onChange={e => setTeamQ(e.target.value)} placeholder="Filtrer par nom ou ville…" className="h-8 text-xs" data-testid="carte-team-search" />
              {teamLoading || !teamData ? (
                <div className="py-10 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-indigo-500" /></div>
              ) : teamData.items.length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center">Aucune proposition enregistrée pour l'instant.</p>
              ) : (
                <>
                  <div className="flex gap-3 text-xs">
                    <span className="rounded-lg bg-indigo-50 dark:bg-indigo-900/20 px-3 py-1.5"><b className="text-indigo-600">{teamData.count}</b> corners proposés</span>
                    <span className="rounded-lg bg-slate-50 dark:bg-slate-800 px-3 py-1.5"><b>{teamData.total_proposals}</b> propositions au total</span>
                  </div>
                  <div className="max-h-[55vh] overflow-auto rounded-lg border border-slate-100 dark:border-slate-800">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 dark:bg-slate-800/60 sticky top-0">
                        <tr>
                          <th className="text-left px-2 py-1.5 font-medium text-slate-500">Corner</th>
                          <th className="text-left px-2 py-1.5 font-medium text-slate-500">Ville</th>
                          <th className="text-right px-2 py-1.5 font-medium text-slate-500">Total</th>
                          <th className="text-left px-2 py-1.5 font-medium text-slate-500">Détail par personne</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teamData.items.filter(it => {
                          const q = teamQ.trim().toLowerCase();
                          return !q || `${it.name || ''} ${it.city || ''}`.toLowerCase().includes(q);
                        }).map(it => (
                          <tr key={it.corner_id} className="border-t border-slate-100 dark:border-slate-800" data-testid="carte-team-row">
                            <td className="px-2 py-1.5 text-slate-700 dark:text-slate-200">{it.name}</td>
                            <td className="px-2 py-1.5 text-slate-500">{it.city || '—'}</td>
                            <td className="px-2 py-1.5 text-right font-bold text-indigo-600 tabular-nums">{it.total}</td>
                            <td className="px-2 py-1.5 text-slate-500">{it.users.map(u => `${(u.email || '').split('@')[0]} (${u.count})`).join(', ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}


      {showAssistant && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="carte-assistant-modal" onClick={() => setShowAssistant(false)}>
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardHeader className="pb-2 flex flex-row items-center justify-between sticky top-0 bg-white dark:bg-slate-900 z-10">
              <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-5 h-5 text-indigo-600" /> Assistant de tournée de prospection</CardTitle>
              <button onClick={() => setShowAssistant(false)} className="text-slate-400" data-testid="carte-assistant-close"><X className="w-4 h-4" /></button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Profil de l'utilisateur (affichage obligatoire) */}
              <div className={`rounded-lg px-3 py-2 border ${wasabiParcMode ? 'bg-orange-50 border-orange-200 dark:bg-orange-900/15 dark:border-orange-900/40' : 'bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-700'}`} data-testid="carte-asst-profile">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Vous êtes : <span className={wasabiParcMode ? 'text-orange-600' : 'text-indigo-600'}>{roleLabel}</span></p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {wasabiParcMode
                    ? "Organisation sur le parc client WASABI : 1 client le matin (9h30) et 1 client l'après-midi (14h). Enseignes verrouillées sur WASABI."
                    : "Selon votre profil, l'organisation de la tournée s'adapte automatiquement."}
                </p>
              </div>
              {/* 1. Départ */}
              <div className="bg-sky-50 dark:bg-sky-900/20 rounded-lg p-3 space-y-1.5">
                <p className="text-xs font-semibold text-sky-700 dark:text-sky-300 flex items-center gap-1"><Flag className="w-3.5 h-3.5" /> 1. Point de départ</p>
                {departure ? (
                  <div className="flex items-center gap-2 text-sm text-sky-800 dark:text-sky-200">
                    <Check className="w-4 h-4" /><span className="flex-1 truncate">{departure.label}</span>
                    <button onClick={() => { setDeparture(null); setDepInput(''); }} className="text-sky-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <Input value={depInput} onChange={e => setDepInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && geocodeDeparture()} placeholder="Mon adresse de départ…" className="h-8 text-xs" data-testid="carte-asst-departure-input" />
                    <Button size="sm" className="h-8 px-3 bg-green-600 hover:bg-green-700 text-white font-semibold" onClick={geocodeDeparture} disabled={locating} data-testid="carte-asst-departure-set">{locating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Valider'}</Button>
                    <Button size="sm" variant="outline" className="h-8 px-2" onClick={useMyPosition} disabled={locating} title="Ma position" data-testid="carte-asst-departure-geo"><Crosshair className="w-3.5 h-3.5" /></Button>
                  </div>
                )}
              </div>
              {/* 2. Marques */}
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-1.5">2. Enseignes à prospecter {wasabiParcMode ? <span className="text-orange-500 font-normal">(verrouillé sur WASABI)</span> : <span className="text-slate-400 font-normal">(aucune cochée = toutes)</span>}</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(brands).map(([b, meta]) => {
                    const on = asstBrands.includes(b);
                    const locked = wasabiParcMode && b !== 'WASABI';
                    return (
                      <button key={b} disabled={locked} onClick={() => { if (!locked) toggleAsstBrand(b); }} className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border ${on ? 'bg-indigo-100 border-indigo-400 text-indigo-700' : 'border-slate-200 dark:border-slate-700 text-slate-500'} ${locked ? 'opacity-40 cursor-not-allowed' : ''}`} data-testid={`carte-asst-brand-${b}`}>
                        <img src={iconUrl(meta.color, meta.icon)} alt="" className="w-3.5 h-3.5" />{b}
                      </button>
                    );
                  })}
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-500 mt-2 cursor-pointer">
                  <input type="checkbox" checked={asstUnvisited} onChange={e => setAsstUnvisited(e.target.checked)} className="w-4 h-4" data-testid="carte-asst-unvisited" />
                  Uniquement les points jamais visités
                </label>
                <div className="mt-2">
                  <p className="text-[11px] font-medium text-slate-500 mb-1">Niveau de proposition par l'outil <span className="text-slate-400">(pour moi)</span></p>
                  <select value={asstMaxProposed} onChange={e => setAsstMaxProposed(e.target.value)} className="w-full px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs" data-testid="carte-asst-proposed">
                    <option value="">Tous (peu importe combien de fois proposés)</option>
                    <option value="0">Jamais proposés</option>
                    <option value="1">Proposés 1 fois maximum</option>
                    <option value="2">Proposés 2 fois maximum</option>
                  </select>
                </div>
                {!wasabiParcMode && gmsList.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                    <p className="text-[11px] font-medium text-slate-500 mb-1.5">Enseignes GMS à <span className="text-rose-600 font-semibold">exclure</span> de la prospection</p>
                    <div className="flex flex-wrap gap-1.5" data-testid="carte-asst-gms-exclude">
                      {gmsList.map(({ group, count }) => {
                        const excluded = excludeGms.has(group);
                        return (
                          <button
                            key={group}
                            onClick={() => toggleExcludeGms(group)}
                            className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border transition-colors ${excluded ? 'bg-rose-100 border-rose-400 text-rose-700 line-through dark:bg-rose-950/40' : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                            data-testid={`carte-asst-gms-${group}`}
                          >
                            {excluded ? <EyeOff className="w-3.5 h-3.5" /> : <Store className="w-3.5 h-3.5" />}
                            {group} <span className="text-slate-400">({count})</span>
                          </button>
                        );
                      })}
                    </div>
                    {excludeGms.size > 0 && (
                      <p className="text-[11px] text-rose-500 mt-1.5">{excludeGms.size} enseigne(s) exclue(s) : ces magasins n'apparaîtront pas dans la tournée.</p>
                    )}
                  </div>
                )}
              </div>
              {/* 3. Jours */}
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> 3. Jours de tournée</p>
                <div className="flex flex-wrap gap-1.5">
                  {[['lundi', 'Lun'], ['mardi', 'Mar'], ['mercredi', 'Mer'], ['jeudi', 'Jeu'], ['vendredi', 'Ven']].map(([d, l]) => {
                    const on = asstDays.includes(d);
                    return (
                      <button key={d} onClick={() => toggleAsstDay(d)} className={`text-xs px-3 py-1.5 rounded-lg border ${on ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-200 dark:border-slate-700 text-slate-500'}`} data-testid={`carte-asst-day-${d}`}>{l}</button>
                    );
                  })}
                </div>
              </div>
              {/* 3b. Semaine cible */}
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {asstCampaign ? 'Semaine de départ de la campagne' : 'Semaine de la tournée'}</p>
                <select value={asstWeekStart} onChange={e => setAsstWeekStart(e.target.value)} className="w-full px-2 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs" data-testid="carte-asst-week">
                  <option value="">{asstCampaign ? 'Cette semaine' : 'Automatique (prochaines dates disponibles)'}</option>
                  {weekOptions.map(o => (
                    <option key={o.iso} value={o.iso}>{o.label}</option>
                  ))}
                </select>
                {!asstCampaign && <p className="text-[11px] text-slate-400 mt-1">Choisissez la semaine à planifier (utile pour espacer plusieurs semaines de tournée).</p>}
              </div>
              {/* 3c. Campagne multi-semaines */}
              <div className="rounded-lg border border-indigo-100 dark:border-indigo-900/40 bg-indigo-50/50 dark:bg-indigo-900/10 p-3 space-y-2">
                <label className="flex items-center gap-2 text-xs font-semibold text-indigo-700 dark:text-indigo-300 cursor-pointer">
                  <input type="checkbox" checked={asstCampaign} onChange={e => setAsstCampaign(e.target.checked)} className="w-4 h-4" data-testid="carte-asst-campaign" />
                  <Sparkles className="w-3.5 h-3.5" /> Campagne multi-semaines (générer plusieurs semaines d'un coup)
                </label>
                {asstCampaign && (
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <div>
                      <p className="text-[11px] text-slate-500 mb-1">Nombre de semaines</p>
                      <select value={asstWeeks} onChange={e => setAsstWeeks(Number(e.target.value))} className="w-full px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs" data-testid="carte-asst-weeks">
                        {[2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n} semaines</option>)}
                      </select>
                    </div>
                    <div>
                      <p className="text-[11px] text-slate-500 mb-1">Espacement</p>
                      <select value={asstInterval} onChange={e => setAsstInterval(Number(e.target.value))} className="w-full px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs" data-testid="carte-asst-interval">
                        <option value={1}>Chaque semaine</option>
                        <option value={2}>1 semaine de pause</option>
                        <option value={3}>2 semaines de pause</option>
                        <option value={4}>3 semaines de pause</option>
                      </select>
                    </div>
                    <p className="col-span-2 text-[11px] text-slate-400">Les corners les plus proches sont répartis sur les semaines <b>sans doublon</b> (chaque corner planifié une seule fois). Un seul plan combiné → un seul export .ics / PDF / email.</p>
                  </div>
                )}
              </div>
              {/* 4. Visites/jour */}
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-1.5">4. Prospections par jour</p>
                {wasabiParcMode ? (
                  <div className="flex items-center gap-2 text-xs bg-orange-50 dark:bg-orange-900/15 border border-orange-200 dark:border-orange-900/40 rounded-lg px-3 py-2" data-testid="carte-asst-perday-locked">
                    <span className="w-8 h-8 rounded-lg bg-orange-500 text-white text-sm font-semibold flex items-center justify-center shrink-0">2</span>
                    <span className="text-slate-600 dark:text-slate-300">1 client le matin · 1 client l'après-midi <span className="text-slate-400">(imposé par votre profil)</span></span>
                  </div>
                ) : (
                  <div className="flex gap-1.5">
                    {[2, 3, 4, 5, 6].map(n => (
                      <button key={n} onClick={() => setAsstPerDay(n)} className={`w-10 h-10 rounded-lg border text-sm font-semibold ${asstPerDay === n ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-200 dark:border-slate-700 text-slate-500'}`} data-testid={`carte-asst-perday-${n}`}>{n}</button>
                    ))}
                  </div>
                )}
              </div>
              {/* 5. Rayon max + créneaux agenda */}
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-1.5">5. Distance maximale du départ</p>
                  <div className="flex flex-wrap gap-1.5">
                    {[[0, 'Illimité'], [25, '25 km'], [50, '50 km'], [100, '100 km'], [150, '150 km']].map(([r, l]) => (
                      <button key={r} onClick={() => setAsstRadius(r)} className={`text-xs px-3 py-1.5 rounded-lg border ${asstRadius === r ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-200 dark:border-slate-700 text-slate-500'}`} data-testid={`carte-asst-radius-${r}`}>{l}</button>
                    ))}
                  </div>
                </div>
                {wasabiParcMode ? (
                  <div className="flex items-start gap-2 text-xs bg-orange-50 dark:bg-orange-900/15 border border-orange-200 dark:border-orange-900/40 rounded-lg px-3 py-2" data-testid="carte-asst-fixed-hours">
                    <CalendarDays className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                    <span className="text-slate-600 dark:text-slate-300">Créneaux fixes : <b>9h30</b> (matin) et <b>14h</b> (après-midi). La durée de visite, la pause déjeuner et les tranches horaires ne s'appliquent pas à votre profil.</span>
                  </div>
                ) : (
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> 6. Horaires de tournée par jour (pour l'agenda Outlook)</p>
                  {asstDays.length === 0 ? (
                    <p className="text-[11px] text-slate-400">Sélectionnez d'abord des jours ci-dessus.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'].filter(d => asstDays.includes(d)).map(d => {
                        const h = getDayHours(d);
                        const label = { lundi: 'Lundi', mardi: 'Mardi', mercredi: 'Mercredi', jeudi: 'Jeudi', vendredi: 'Vendredi' }[d];
                        return (
                          <div key={d} className="flex items-center gap-2 text-xs" data-testid={`carte-asst-hours-${d}`}>
                            <span className="w-16 text-slate-600 dark:text-slate-300">{label}</span>
                            <span className="text-slate-400">de</span>
                            <select value={h.start} onChange={e => setDayHour(d, 'start', +e.target.value)} className="px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900" data-testid={`carte-asst-start-${d}`}>
                              {[6, 7, 8, 9, 10, 11, 12, 13, 14].map(x => <option key={x} value={x}>{x}h</option>)}
                            </select>
                            <span className="text-slate-400">à</span>
                            <select value={h.end} onChange={e => setDayHour(d, 'end', +e.target.value)} className="px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900" data-testid={`carte-asst-end-${d}`}>
                              {[12, 13, 14, 15, 16, 17, 18, 19, 20].filter(x => x > h.start).map(x => <option key={x} value={x}>{x}h</option>)}
                            </select>
                          </div>
                        );
                      })}
                      <div className="flex items-center gap-2 text-xs text-slate-500 pt-1">
                        <span>Durée max/visite</span>
                        <select value={asstVisitMin} onChange={e => setAsstVisitMin(+e.target.value)} className="px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900" data-testid="carte-asst-visit-min">
                          {[30, 45, 60, 90, 120].map(m => <option key={m} value={m}>{m} min</option>)}
                        </select>
                        <span className="text-slate-400">(les visites sont réparties sur la plage du jour)</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 pt-1">
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input type="checkbox" checked={asstLunch} onChange={e => setAsstLunch(e.target.checked)} className="w-4 h-4" data-testid="carte-asst-lunch" />
                          Pause déjeuner
                        </label>
                        {asstLunch && (
                          <>
                            <span>à</span>
                            <select value={asstLunchStart} onChange={e => setAsstLunchStart(+e.target.value)} className="px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900" data-testid="carte-asst-lunch-start">
                              {[[720, '12h00'], [750, '12h30'], [780, '13h00'], [810, '13h30']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                            <span>pendant</span>
                            <select value={asstLunchMin} onChange={e => setAsstLunchMin(+e.target.value)} className="px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900" data-testid="carte-asst-lunch-min">
                              {[30, 45, 60, 90].map(m => <option key={m} value={m}>{m} min</option>)}
                            </select>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>
              <div className="text-xs text-slate-500 bg-slate-50 dark:bg-slate-800 rounded p-2">
                {asstCampaign ? (
                  <>Capacité : <b>{asstDays.length * asstPerDay * asstWeeks}</b> visites ({asstWeeks} semaines × {asstDays.length} jour{asstDays.length > 1 ? 's' : ''} × {asstPerDay}). Réparties sans doublon sur les semaines.</>
                ) : (
                  <>Capacité : <b>{asstDays.length * asstPerDay}</b> visites ({asstDays.length} jour{asstDays.length > 1 ? 's' : ''} × {asstPerDay}). L'outil choisit automatiquement les points les plus proches de votre départ.</>
                )}
              </div>
              <Button className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={runAssistant} disabled={asstRunning} data-testid="carte-asst-run">
                {asstRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}<span className="ml-1.5">{asstCampaign ? 'Lancer la campagne multi-semaines' : 'Lancer la tournée intelligente'}</span>
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
